import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Modal,
  Spinner,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  InputGroup,
  Input,
  Alert
} from 'reactstrap';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { workstatusOptions, currencies, formatDateTimeForAPI, formatEnumName } from '../constants/utils';
import AssetUsageStatusFields from './AssetUsageStatusFields';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../helpers/api';
import FeatherIcon from 'feather-icons-react';
import { translateActivity } from '../helpers/activityTranslator';
import PreventiveModal from './PreventiveModal';
import { getPresignedUrl, uploadServiceContractFiles, validateFiles } from '../helpers/fileUpload';
import usePermission from '../Hooks/usePermission';
import { ASSET_TRANSFER } from '../constants/permissions';
import AssetStatusChangeModal from './AssetStatusChangeModal';
import AssetTransferModal from './AssetTransferModal';
import TransferDetailsModal from './planning/TransferDetailsModal';
import LocationModal from './LocationModal';
import MeterDetailModal from '../Page/MeterDetail';
import AssetTCOPanel from './AssetTCOPanel';
import AssetCostRollupCard from './AssetCostRollupCard';

const METER_STATUS_COLORS = {
  PENDING: 'secondary',
  STABLE: 'success',
  WARNING: 'warning',
  ALARM: 'danger',
  OVERDUE: 'info',
};

const AssetModal = ({
  isOpen,
  toggle,
  userData,
  loading,
  formatCreatedDate,
  onTicketClick,
  fetchUserWorkOrders,
  onEditAsset = null, // Function to handle asset edit (used for sub-asset row click)
  onCreateSubAsset = null, // Function to open the unified create form pre-filled with this asset as parent
  initialActiveTab = null, // Optional tab to focus when the modal opens (e.g. 'subAssets' from the list chip)
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isRequestor, hasPermission } = usePermission();
  const canTransferAsset = hasPermission(ASSET_TRANSFER);
  const [activeTab, setActiveTab] = useState('assetDetails');
  const [assetData, setAssetData] = useState(userData); // Local state for asset data
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferDetails, setTransferDetails] = useState({ isOpen: false, transfer: null });
  const [transferByProcess, setTransferByProcess] = useState({}); // process id -> parent transfer request
  const [planningLocations, setPlanningLocations] = useState([]);
  const [planningLocationModal, setPlanningLocationModal] = useState({ isOpen: false, title: '', mainData: null, relatedData: [], loading: false, locationData: null });

  // Pre-fetch the parent transfer requests (for a clickable Request ID + full modal data) and the
  // org's locations (for the location modal), so the Planning table matches the main Planning page.
  useEffect(() => {
    if (!assetData?.equipmentProcesses?.length) return undefined;
    const orgId = assetData?.organization?.id
      ?? JSON.parse(localStorage.getItem('authUser') || '{}')?.activeOrganizationId
      ?? null;
    if (!orgId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('equipment/getAllEquipmentRequests', {
          params: { requestType: 'TRANSFER', count: 1000, organizationId: orgId },
        });
        if (!cancelled) {
          const map = {};
          (data?.response || []).forEach((r) => (r.processInfo || []).forEach((p) => { map[p.id] = r; }));
          setTransferByProcess(map);
        }
      } catch (e) { /* ignore */ }
      try {
        const { data } = await api.get(`organization-location/get-by-organization-id?organizationId=${orgId}&count=9999&hierarchy=false`);
        if (!cancelled) setPlanningLocations(data?.response?.[0]?.locations || []);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [assetData?.id, assetData?.equipmentProcesses?.length, assetData?.organization?.id]);

  // Open the shared transfer-details modal (same as the Planning page) for a Planning row.
  const openTransferDetails = useCallback((issue) => {
    const parent = transferByProcess[issue.id];
    const synthetic = {
      status: issue.status,
      requestDate: issue.createdAt || issue.startDate,
      processInfo: [{
        id: issue.id,
        linkedAsset: issue.linkedAsset || { id: assetData?.id, name: assetData?.assetName },
        fromLocationsInfo: issue.fromLocationsInfo,
        locationsInfo: issue.locationsInfo,
        startDate: issue.startDate,
        endDate: issue.endDate,
        reason: issue.reason,
        status: issue.status,
      }],
    };
    setTransferDetails({ isOpen: true, transfer: parent || synthetic });
  }, [transferByProcess, assetData?.id, assetData?.assetName]);

  // Open the location modal for a clicked location name (mirrors the Planning page behavior).
  const openPlanningLocation = useCallback(async (locationName) => {
    if (!locationName) return;
    let locationData = null;
    const found = planningLocations.find((loc) => loc.locationName === locationName || loc.subLocations?.some((s) => s.locationName === locationName));
    if (found) {
      locationData = found.locationName === locationName
        ? { ...found, organization: assetData?.organization }
        : { ...found.subLocations.find((s) => s.locationName === locationName), organization: assetData?.organization };
    }
    setPlanningLocationModal({ isOpen: true, title: t('shared.location_details', 'Location Details'), mainData: locationName, relatedData: [], loading: true, locationData });
    try {
      const { data } = await api.get('asset-issues/organization/filtered', { params: { organizationLocation: locationName } });
      setPlanningLocationModal((prev) => ({ ...prev, relatedData: data?.response || [], loading: false }));
    } catch (e) {
      setPlanningLocationModal((prev) => ({ ...prev, loading: false }));
    }
  }, [planningLocations, assetData?.organization, t]);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);

  // Sub Assets states
  const [subAssets, setSubAssets] = useState([]);
  const [loadingSubAssets, setLoadingSubAssets] = useState(false);
  const [subAssetDeleteModal, setSubAssetDeleteModal] = useState({
    isOpen: false,
    subAsset: null
  });
  // Tree state — nodes expanded in the Sub Assets tree, lazy-loaded children cache, in-flight fetches.
  const [expandedNodeIds, setExpandedNodeIds] = useState(() => new Set());
  const [childrenByParentId, setChildrenByParentId] = useState({});
  const [loadingNodeIds, setLoadingNodeIds] = useState(() => new Set());
  const [hoveredNodeId, setHoveredNodeId] = useState(null);

  // Warranty Management States
  const [warranties, setWarranties] = useState([]);
  const [loadingWarranties, setLoadingWarranties] = useState(false);
  const [warrantyModal, setWarrantyModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedWarranty, setSelectedWarranty] = useState(null);
  const [warrantyToDelete, setWarrantyToDelete] = useState(null);
  const [warrantyErrors, setWarrantyErrors] = useState({});
  const [warrantyApiError, setWarrantyApiError] = useState("");
  const [savedVendors, setSavedVendors] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [warrantySearchTerm, setWarrantySearchTerm] = useState("");
  const [uploadingImages, setUploadingImages] = useState(false);

  // Service Requests states (for requestors)
  const [serviceRequests, setServiceRequests] = useState([]);
  const [loadingServiceRequests, setLoadingServiceRequests] = useState(false);
  const [selectedServiceRequest, setSelectedServiceRequest] = useState(null);

  // Document upload states
  const documentInputRef = useRef(null);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState(null);
  const [documentErrors, setDocumentErrors] = useState([]);

  // Assign Service Contract States
  const [assignContractModal, setAssignContractModal] = useState(false);
  const [allServiceContracts, setAllServiceContracts] = useState([]);
  const [selectedContractId, setSelectedContractId] = useState("");
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [assignContractError, setAssignContractError] = useState("");
  const [assigningContract, setAssigningContract] = useState(false);

  // All Work Orders State
  const [allWorkOrders, setAllWorkOrders] = useState([]);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false);

  // Preventive Modal State
  const [preventiveModal, setPreventiveModal] = useState({
    isOpen: false,
    workOrder: null
  });

    // Spare Parts States
    const [sparePartsUsage, setSparePartsUsage] = useState([]);
    const [loadingSparePartsConsumables, setLoadingSparePartsConsumables] = useState(false);
    const [showAttachSparePartModal, setShowAttachSparePartModal] = useState(false);
    const [availableSpareParts, setAvailableSpareParts] = useState([]);
    const [selectedSparePartId, setSelectedSparePartId] = useState('');
    const [attachQuantity, setAttachQuantity] = useState(1);
    const [attachLoading, setAttachLoading] = useState(false);

    // Meters States
    const [assetMeters, setAssetMeters] = useState([]);
    const [loadingMeters, setLoadingMeters] = useState(false);
    const [detailMeterId, setDetailMeterId] = useState(null);

  // Sync assetData with userData prop when it changes
  useEffect(() => {
    setAssetData(userData);
  }, [userData]);

  // Swap the asset rendered by this modal in-place — used by sub-asset name clicks in the tree.
  // Updates assetData and fetches the full record so all tabs/sections render complete data.
  const swapViewedAsset = async (asset) => {
    if (!asset?.id) return;
    setActiveTab('assetDetails');
    setAssetData(asset);
    try {
      const { data } = await api.get(`asset/get-by-id?id=${asset.id}`);
      if (data?.response) setAssetData(data.response);
    } catch (err) {
      console.error('Failed to fetch full asset on swap:', err);
    }
  };

    // Fetch spare parts usage
  const fetchSparePartsAndConsumables = async () => {
    if (!assetData?.id) return;

    try {
      setLoadingSparePartsConsumables(true);
      const response = await api.get(`spare-parts/by-asset?assetId=${assetData.id}`);
      setSparePartsUsage(response?.data?.response || []);
    } catch (error) {
      console.error('Error fetching spare parts:', error);
      setSparePartsUsage([]);
    } finally {
      setLoadingSparePartsConsumables(false);
    }
  };

  // Open attach spare part modal
  const handleOpenAttachSparePart = async () => {
    try {
      const orgId = assetData?.organization?.id;
      if (!orgId) return;
      const res = await api.get(`spare-parts/all?organizationId=${orgId}`);
      setAvailableSpareParts(res?.data?.response || []);
      setSelectedSparePartId('');
      setAttachQuantity(1);
      setShowAttachSparePartModal(true);
    } catch (err) {
      console.error('Failed to fetch spare parts:', err);
    }
  };

  // Submit attach spare part
  const handleAttachSparePart = async () => {
    if (!selectedSparePartId || attachQuantity < 1 || !assetData?.id) return;
    try {
      setAttachLoading(true);
      await api.post('spare-parts/attach-to-asset', {
        assetId: assetData.id,
        inventory: [{ sparePartId: Number(selectedSparePartId), quantity: attachQuantity }]
      });
      setShowAttachSparePartModal(false);
      fetchSparePartsAndConsumables();
    } catch (err) {
      console.error('Failed to attach spare part:', err);
    } finally {
      setAttachLoading(false);
    }
  };

  // Fetch meters for this asset
  const fetchAssetMeters = async () => {
    if (!assetData?.id) return;
    try {
      setLoadingMeters(true);
      const response = await api.get(`meter/by-asset?assetId=${assetData.id}`);
      setAssetMeters(response?.data?.response || []);
    } catch (error) {
      console.error('Error fetching meters:', error);
      setAssetMeters([]);
    } finally {
      setLoadingMeters(false);
    }
  };


  // Enhanced animations
  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      y: -50
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2
      }
    }
  };

  const getActivity = async () => {
    try {
      setLoadingActivities(true);
      const { data } = await api.get(`asset/activity?parentId=${assetData?.id}`);
      setActivities(data.response || []);
    } catch (error) {
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  // Warranty Management Functions
  const getWarranties = async () => {
    if (!assetData?.id) return;

    try {
      setLoadingWarranties(true);
      const { data } = await api.get(
        `asset-warranty-maintenance/get-all?assetId=${assetData.id}${
          warrantySearchTerm ? `&search=${warrantySearchTerm}` : ""
        }`
      );
      setWarranties(data.response || []);
    } catch (error) {
      console.error('Error fetching warranties:', error);
      setWarranties([]);
    } finally {
      setLoadingWarranties(false);
    }
  };

  // Fetch all service contracts for assignment
  const fetchAllServiceContracts = async () => {
    try {
      setLoadingContracts(true);
      const response = await api.get('asset-warranty-maintenance/get-all?count=9999');
      setAllServiceContracts(response?.data?.response || []);
    } catch (error) {
      console.error('Error fetching service contracts:', error);
      setAllServiceContracts([]);
    } finally {
      setLoadingContracts(false);
    }
  };

  // Handle assign service contract modal
  const handleAssignContractModal = () => {
    if (!assignContractModal) {
      fetchAllServiceContracts();
    }
    setAssignContractModal(!assignContractModal);
    setSelectedContractId("");
    setAssignContractError("");
  };

  // Handle assign service contract submission
  const handleAssignContract = async () => {
    if (!selectedContractId) {
      setAssignContractError("Please select a service contract");
      return;
    }

    try {
      setAssigningContract(true);
      setAssignContractError("");

      await api.patch(`asset-warranty-maintenance/add-asset?warrantyId=${selectedContractId}&assetId=${assetData.id}`);

      // Refresh warranties after assignment
      await getWarranties();
      handleAssignContractModal();
    } catch (error) {
      console.error('Error assigning service contract:', error);
      setAssignContractError(error?.response?.data?.error || "Failed to assign service contract");
    } finally {
      setAssigningContract(false);
    }
  };


  const fetchVendors = async() => {
    try {
        const {data} = await api.get(`vendor/getAllVendors`);
        setVendors(data.response || []);
    } catch (error) {
        console.error('Error fetching vendors:', error);
        setVendors([]);
    }
  };

  const handleWarrantyModal = (warranty = null) => {
    setWarrantyModal(!warrantyModal);
    
    if (warranty) {
      // Find vendor ID from vendor name if editing existing warranty
      const vendorId = vendors.find(v => v.vendorName === warranty.maintenanceVendorName)?.id || '';
      setSelectedWarranty({
        ...warranty,
        vendorId: vendorId
      });
    } else {
      setSelectedWarranty(null);
    }
    
    setWarrantyErrors({});
    setWarrantyApiError("");
  };

  const handleDeleteModal = (warranty = null) => {
    setDeleteModal(!deleteModal);
    setWarrantyToDelete(warranty);
  };

  const handleDelete = async () => {
      try {
          await api.delete(`asset-warranty-maintenance/delete/${warrantyToDelete?.id}`);
          setDeleteModal(false);
          getWarranties()
      } catch (error) {
          console.error('Error deleting asset:', error);
          setWarrantyApiError('Failed to delete asset');
      }
  }

  const handleWarrantyUpdate = async (e) => {
  e.preventDefault();
  
  // Clear previous errors
  setWarrantyErrors({});
  setWarrantyApiError("");
  
  // Check if selectedWarranty exists
  if (!selectedWarranty) {
    setWarrantyApiError("Please fill in the warranty information");
    return;
  }
  
  const newErrors = {};
  
  // Manufacturer Warranty validations
  if (selectedWarranty.hasManufacturerWarranty) {
    if (!selectedWarranty.manufacturerWarrantyStartDate || !selectedWarranty.manufacturerWarrantyStartDate.trim()) {
      newErrors.manufacturerWarrantyStartDate = "Warranty start date is required";
    }
    if (!selectedWarranty.manufacturerWarrantyEndDate || !selectedWarranty.manufacturerWarrantyEndDate.trim()) {
      newErrors.manufacturerWarrantyEndDate = "Warranty end date is required";
    }
    
    // Additional date validation - end date should be after start date
    if (selectedWarranty.manufacturerWarrantyStartDate && selectedWarranty.manufacturerWarrantyEndDate) {
      const startDate = new Date(selectedWarranty.manufacturerWarrantyStartDate);
      const endDate = new Date(selectedWarranty.manufacturerWarrantyEndDate);
      if (endDate <= startDate) {
        newErrors.manufacturerWarrantyEndDate = "End date must be after start date";
      }
    }
  }
  
  // Warranty Extension validations
  if (selectedWarranty.hasWarrantyExtension) {
    if (!selectedWarranty.warrantyExtensionStartDate || !selectedWarranty.warrantyExtensionStartDate.trim()) {
      newErrors.warrantyExtensionStartDate = "Extension start date is required";
    }
    if (!selectedWarranty.warrantyExtensionEndDate || !selectedWarranty.warrantyExtensionEndDate.trim()) {
      newErrors.warrantyExtensionEndDate = "Extension end date is required";
    }
    
    // Additional date validation - end date should be after start date
    if (selectedWarranty.warrantyExtensionStartDate && selectedWarranty.warrantyExtensionEndDate) {
      const startDate = new Date(selectedWarranty.warrantyExtensionStartDate);
      const endDate = new Date(selectedWarranty.warrantyExtensionEndDate);
      if (endDate <= startDate) {
        newErrors.warrantyExtensionEndDate = "Extension end date must be after start date";
      }
    }
  }
  
  // Maintenance Contract validations
  if (selectedWarranty.maintenanceContractType && selectedWarranty.maintenanceContractType !== "NO_CONTRACT") {
    if (!selectedWarranty.vendorId) {
      newErrors.vendorId = "Vendor is required";
    }
    if (!selectedWarranty.contractNumber || !selectedWarranty.contractNumber.trim()) {
      newErrors.contractNumber = "Contract number is required";
    }
    if (!selectedWarranty.contractStartDate || !selectedWarranty.contractStartDate.trim()) {
      newErrors.contractStartDate = "Contract start date is required";
    }
    if (!selectedWarranty.contractEndDate || !selectedWarranty.contractEndDate.trim()) {
      newErrors.contractEndDate = "Contract end date is required";
    }
    if (!selectedWarranty.contractValue || selectedWarranty.contractValue <= 0) {
      newErrors.contractValue = "Contract value must be greater than 0";
    }
    
    // Additional date validation for contract dates
    if (selectedWarranty.contractStartDate && selectedWarranty.contractEndDate) {
      const startDate = new Date(selectedWarranty.contractStartDate);
      const endDate = new Date(selectedWarranty.contractEndDate);
      if (endDate <= startDate) {
        newErrors.contractEndDate = "Contract end date must be after start date";
      }
    }
  }
  
  // If there are validation errors, set them and stop execution
  if (Object.keys(newErrors).length > 0) {
    setWarrantyErrors(newErrors);
    return;
  }

  const updatedWarranty = {
    ...selectedWarranty,
    assetId: assetData.id,
    contractCurrency: selectedWarranty?.contractCurrency || 'USD',
    hasManufacturerWarranty: selectedWarranty?.hasManufacturerWarranty || false,
    hasWarrantyExtension: selectedWarranty?.hasWarrantyExtension || false,
    maintenanceContractType: selectedWarranty?.maintenanceContractType || 'NO_CONTRACT',
    attachmentUrls: selectedWarranty?.attachmentUrls || []
  };

  try {
    const isCreate = !selectedWarranty.id;
    const endpoint = isCreate ? "asset-warranty-maintenance/create" : "asset-warranty-maintenance/update";

    if (isCreate) {
      await api.post(endpoint, updatedWarranty);
    } else {
      await api.put(endpoint, updatedWarranty);
    }
    
    // Save vendor name if it's new
    const selectedVendor = vendors.find(v => v.id === selectedWarranty.vendorId);
    if (selectedVendor &&
        selectedWarranty.maintenanceContractType !== "NO_CONTRACT" &&
        !savedVendors.includes(selectedVendor.vendorName)) {
      setSavedVendors([...savedVendors, selectedVendor.vendorName]);
    }
    
    getWarranties();
    handleWarrantyModal();
  } catch (error) {
    setWarrantyApiError(error?.response?.data?.message || "Something went wrong!");
  }
};

  const handleWarrantyDelete = async () => {
    try {
      // TODO: Add your API call here
      // await api.delete(`asset-warranty-maintenance/delete/${warrantyToDelete.id}`);
      
      // For now, just close the modal and refresh data
      handleDeleteModal();
      getWarranties();
    } catch (error) {
      console.error('Error deleting warranty:', error);
    }
  };

  // Sub Assets Functions
  const getSubAssets = async () => {
    if (!assetData?.id) return;
    setLoadingSubAssets(true);
    try {
      // Always fetch the latest record so we don't depend on whether userData was passed with children populated.
      const { data } = await api.get(`asset/get-by-id?id=${assetData.id}`);
      setSubAssets(data?.response?.children || []);
    } catch (error) {
      console.error('Error fetching sub assets:', error);
      setSubAssets(assetData?.children || []);
    } finally {
      setLoadingSubAssets(false);
    }
  };

  // Sub-asset name click in the tree → view that sub-asset in this same modal (no nested modal, no edit form).
  const handleSubAssetClick = (subAsset) => {
    swapViewedAsset(subAsset);
  };

  // Lazy-fetch a node's children when it's first expanded. Cached afterwards.
  const fetchNodeChildren = async (nodeId) => {
    if (childrenByParentId[nodeId] !== undefined) return;
    setLoadingNodeIds((prev) => { const next = new Set(prev); next.add(nodeId); return next; });
    try {
      const { data } = await api.get(`asset/get-by-id?id=${nodeId}`);
      const children = data?.response?.children || [];
      setChildrenByParentId((prev) => ({ ...prev, [nodeId]: children }));
    } catch (err) {
      console.error('Error fetching children for node', nodeId, err);
      setChildrenByParentId((prev) => ({ ...prev, [nodeId]: [] }));
    } finally {
      setLoadingNodeIds((prev) => { const next = new Set(prev); next.delete(nodeId); return next; });
    }
  };

  const toggleTreeNode = (nodeId) => {
    setExpandedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
        fetchNodeChildren(nodeId);
      }
      return next;
    });
  };

  const handleCreateUnderNode = (parentNode) => {
    if (!parentNode?.id) return;
    toggle();
    if (typeof onCreateSubAsset === 'function') {
      onCreateSubAsset(parentNode);
    } else {
      navigate(`/assets?createSubAssetOf=${parentNode.id}`);
    }
  };

  // Reset tree state whenever the viewed asset changes (open or in-place swap).
  useEffect(() => {
    setExpandedNodeIds(new Set());
    setChildrenByParentId({});
    setLoadingNodeIds(new Set());
  }, [assetData?.id]);

  // "+ Create Sub-Asset" — route through the unified create form pre-filled with this asset as parent.
  // Prefers the parent-page callback; otherwise deep-links to the Assets page.
  const handleCreateSubAssetClick = () => {
    toggle();
    if (typeof onCreateSubAsset === 'function') {
      onCreateSubAsset(assetData);
    } else if (assetData?.id) {
      navigate(`/assets?createSubAssetOf=${assetData.id}`);
    }
  };

  const handleDeleteSubAsset = (subAsset) => {
    setSubAssetDeleteModal({
      isOpen: true,
      subAsset: subAsset
    });
  };

  // Recursive tree node renderer — used inside the Sub Assets tab.
  const renderTreeNode = (node, depth = 0) => {
    const expanded = expandedNodeIds.has(node.id);
    const cached = childrenByParentId[node.id];
    const loading = loadingNodeIds.has(node.id);
    const knownChildren = cached !== undefined ? cached : (node.children || []);
    // Show chevron when we already know there are children, OR when we haven't checked yet.
    const showChevron = (cached === undefined && (node.children?.length ?? 1) > 0)
      || (cached !== undefined && cached.length > 0);
    const isHovered = hoveredNodeId === node.id;
    const statusKey = node?.assetStatus ? node.assetStatus.toLowerCase() : null;

    return (
      <div key={node.id}>
        <div
          className="d-flex align-items-center gap-2 py-2 pe-2 rounded"
          style={{
            paddingLeft: depth * 40 + 8,
            background: isHovered ? '#f8f9fa' : 'transparent',
            transition: 'background 0.15s ease',
          }}
          onMouseEnter={() => setHoveredNodeId(node.id)}
          onMouseLeave={() => setHoveredNodeId((prev) => prev === node.id ? null : prev)}
        >
          {showChevron ? (
            <button
              type="button"
              className="btn btn-sm p-0 border-0 bg-transparent"
              style={{ width: 24, height: 24, lineHeight: 1 }}
              onClick={() => toggleTreeNode(node.id)}
              aria-label={expanded ? t('shared.collapse', 'Collapse') : t('shared.expand', 'Expand')}
            >
              {loading
                ? <Spinner size="sm" />
                : <FeatherIcon icon={expanded ? 'chevron-down' : 'chevron-right'} size={16} />}
            </button>
          ) : (
            <span style={{ display: 'inline-block', width: 24 }} />
          )}
          <i className="bx bx-sitemap text-info" style={{ fontSize: 18 }}></i>
          <span
            role="button"
            className="text-primary fw-medium flex-grow-1 text-truncate"
            style={{ cursor: 'pointer' }}
            onClick={() => handleSubAssetClick(node)}
            title={node.assetName}
          >
            {node.assetName}
          </span>
          {/* Inline "+ Sub Asset" — visible on hover. */}
          {isHovered && (
            <button
              type="button"
              className="btn btn-sm btn-link text-primary p-0 d-inline-flex align-items-center gap-1"
              onClick={() => handleCreateUnderNode(node)}
              title={t('shared.add_sub_asset', 'Add Sub Asset')}
              style={{ textDecoration: 'none' }}
            >
              <FeatherIcon icon="plus-circle" size={14} />
              <span style={{ fontSize: 12 }}>{t('shared.sub_asset', 'Sub Asset')}</span>
            </button>
          )}
          {node?.assetStatus && (
            <Badge color={getAssetStatusBadge(node.assetStatus)} className="px-2 py-1" style={{ fontSize: 10 }}>
              {statusKey ? t(`shared.${statusKey}`, node.assetStatus) : t('shared.n_a', 'N/A')}
            </Badge>
          )}
          <div className="d-flex gap-1">
            <Button
              color="outline-warning"
              size="sm"
              className="btn-sm-icon"
              onClick={() => handleSubAssetClick(node)}
              title={t('shared.button_edit_sub_asset', 'Edit sub asset')}
              style={{ padding: '4px 8px' }}
            >
              <FeatherIcon icon="edit" size={12} />
            </Button>
            <Button
              color="outline-danger"
              size="sm"
              className="btn-sm-icon"
              onClick={() => handleDeleteSubAsset(node)}
              title={t('shared.button_delete_sub_asset', 'Delete sub asset')}
              style={{ padding: '4px 8px' }}
            >
              <FeatherIcon icon="trash-2" size={12} />
            </Button>
          </div>
        </div>
        {expanded && (
          <>
            {knownChildren.length === 0 && cached !== undefined && !loading && (
              <div
                className="text-muted small py-1"
                style={{ paddingLeft: (depth + 1) * 40 + 32 }}
              >
                {t('business.assets.no_nested_sub_assets', 'No further sub-assets.')}
              </div>
            )}
            {knownChildren.map((child) => renderTreeNode(child, depth + 1))}
          </>
        )}
      </div>
    );
  };

  const confirmDeleteSubAsset = async () => {
    setLoadingSubAssets(true);

    try {
      await api.delete(`asset?assetId=${subAssetDeleteModal.subAsset.id}`);
      
      // Refresh sub assets
      const user = JSON.parse(localStorage.getItem('authUser'));
      const organizationId = user?.activeOrganizationId ?? null;

      const updatedAssetsData = await api.get(`asset/all`, {
        params: {
          organizationId: organizationId,
          count: 9999
        }
      });
      const updatedParentAsset = updatedAssetsData.data.response[0]?.assets?.find(
        asset => asset.id === assetData.id
      );

      if (updatedParentAsset) {
        setSubAssets(updatedParentAsset.children || []);
      }

      setSubAssetDeleteModal({
        isOpen: false,
        subAsset: null
      });
    } catch (error) {
      console.error('Error deleting sub asset:', error);
    } finally {
      setLoadingSubAssets(false);
    }
  };

  const tableRowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        type: "spring",
        stiffness: 300
      }
    })
  };

    useEffect(() => {
      if (assetData?.id && activeTab === 'spareParts') {
        fetchSparePartsAndConsumables();
      }
    }, [assetData?.id, activeTab]);

    useEffect(() => {
      if (assetData?.id && activeTab === 'meters') {
        fetchAssetMeters();
      }
    }, [assetData?.id, activeTab]);

  // Document display helper functions
  const getDocumentIcon = (url) => {
    const ext = url?.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return 'file-text';
      case 'doc':
      case 'docx':
        return 'file';
      case 'xls':
      case 'xlsx':
        return 'grid';
      default:
        return 'file';
    }
  };

  const getFileNameFromUrl = (url) => {
    try {
      const pathname = new URL(url).pathname;
      return decodeURIComponent(pathname.split('/').pop() || 'Document');
    } catch {
      return url?.split('/').pop() || 'Document';
    }
  };

  // Refresh asset data after upload/delete
  const refreshAssetData = async () => {
    if (!assetData?.id || assetData?.record === false) return;
    try {
      const { data } = await api.get(`asset/get-by-id?id=${assetData.id}`);
      if (data?.response) {
        setAssetData(data.response);
      }
    } catch (error) {
      console.error('Error refreshing asset data:', error);
    }
  };

  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate files before upload
    const validation = validateFiles(files);

    if (validation.errors.length > 0) {
      setDocumentErrors(validation.errors);
      setTimeout(() => setDocumentErrors([]), 5000);
    }

    // Only proceed if there are valid files
    if (validation.validFiles.length === 0) {
      if (documentInputRef.current) {
        documentInputRef.current.value = '';
      }
      return;
    }

    setUploadingDocuments(true);
    try {
      const uploadedUrls = await uploadServiceContractFiles(validation.validFiles);

      if (uploadedUrls && uploadedUrls.length > 0) {
        // Categorize uploaded files - only documents for this tab
        const newDocumentUrls = uploadedUrls.filter(url =>
          !/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url)
        );

        // Update asset with new documents - spread full assetData to satisfy backend requirements
        const payload = {
          ...assetData,
          documentUrls: [...(assetData?.documentUrls || []), ...newDocumentUrls],
          currentLocation: assetData?.locationId
        };

        await api.put(`asset/update?assetId=${assetData.id}`, payload);
        await refreshAssetData(); // Refresh to show new documents
      }
    } catch (error) {
      console.error('Error uploading documents:', error);
      setDocumentErrors([t('shared.failed_upload', 'Failed to upload files. Please try again.')]);
      setTimeout(() => setDocumentErrors([]), 5000);
    } finally {
      setUploadingDocuments(false);
      if (documentInputRef.current) {
        documentInputRef.current.value = '';
      }
    }
  };

  const dismissDocumentError = (index) => {
    setDocumentErrors(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteDocument = async (url) => {
    setDeletingDocument(url);
    try {
      // Spread full assetData to satisfy backend requirements
      const payload = {
        ...assetData,
        documentUrls: (assetData?.documentUrls || []).filter(u => u !== url),
        currentLocation: assetData?.locationId
      };

      await api.put(`asset/update?assetId=${assetData.id}`, payload);
      await refreshAssetData(); // Refresh to update display
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setDeletingDocument(null);
    }
  };

  const applyDateFilters = () => {
    if (!assetData?.id) return;

    setIsFiltering(true);

    const adjustedFromDate = fromDate ?
      new Date(Date.UTC(
        fromDate.getFullYear(),
        fromDate.getMonth(),
        fromDate.getDate(),
        0, 0, 0
      )) : null;

    const adjustedToDate = toDate ?
      new Date(Date.UTC(
        toDate.getFullYear(),
        toDate.getMonth(),
        toDate.getDate(),
        23, 59, 59
      )) : null;

    fetchAllWorkOrders(adjustedFromDate, adjustedToDate)
      .finally(() => setIsFiltering(false));
  };

  const clearDateFilters = () => {
    setFromDate(null);
    setToDate(null);
    setSearchTerm('');
    if (!assetData?.id) return;

    setIsFiltering(true);
    fetchAllWorkOrders(null, null)
      .finally(() => setIsFiltering(false));
  };

  // Fetch service requests for this asset (requestor only)
  const fetchServiceRequests = useCallback(async () => {
    if (!assetData?.id || !isRequestor) return;
    setLoadingServiceRequests(true);
    try {
      const { data } = await api.get('service-request/my-requests?count=0');
      const allRequests = data?.response || [];
      // Filter to only requests for this asset
      const assetRequests = allRequests.filter(
        req => req.asset?.id === assetData.id
      );
      setServiceRequests(assetRequests);
    } catch (error) {
      console.error('Error fetching service requests:', error);
      setServiceRequests([]);
    } finally {
      setLoadingServiceRequests(false);
    }
  }, [assetData?.id, isRequestor]);

  // Fetch all work orders from the unified asset_issues table.
  // workType (REACTIVE / PREVENTIVE / CALIBRATION / TESTING / ROUTINE / RECALL)
  // is stored on the row, so a single endpoint covers every type.
  const fetchAllWorkOrders = async (fromDate = null, toDate = null) => {
    if (!assetData?.id) return;

    setLoadingWorkOrders(true);

    try {
      const assetId = assetData.id;
      const params = { assetId };

      if (fromDate) params.fromDate = formatDateTimeForAPI(fromDate);
      if (toDate) params.toDate = formatDateTimeForAPI(toDate);

      const { data } = await api.get(`asset-issues/organization/filtered`, { params });
      const items = data?.response || [];

      const toTitle = (s) =>
        typeof s === 'string' && s.length > 0
          ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
          : s;

      const allOrders = items.map(item => ({
        ...item,
        workType: toTitle(item.workType) || 'Reactive',
        ticketNumber: item.ticketNumber || item.requestId,
        requestDate: item.requestDate || item.dueDate || item.createdAt,
      }));

      allOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setAllWorkOrders(allOrders);
    } catch (error) {
      console.error('Error fetching all work orders:', error);
      setAllWorkOrders([]);
    } finally {
      setLoadingWorkOrders(false);
    }
  };

  // Handle preventive work order click
  const handlePreventiveWorkOrderClick = async (workOrder) => {
    try {
      const { data } = await api.get('preventive/getAllMasterWO', {
        params: { search: '' }
      });
      // Find the parent work order that contains this sub work order
      const parentWorkOrder = data?.response?.find(wo =>
        wo.subWorkOrders?.some(subWO => subWO.id === workOrder.id)
      );
      if (parentWorkOrder) {
        setPreventiveModal({
          isOpen: true,
          workOrder: parentWorkOrder
        });
      }
    } catch (error) {
      console.error('Error fetching preventive work order:', error);
    }
  };

  // Initial data loading
  useEffect(() => {
    if (assetData?.id) {
      getActivity();
      fetchVendors();
    }
  }, [assetData?.id]);

  // Tab-specific data loading
  useEffect(() => {
    if (assetData?.id && activeTab === 'warranties') {
      getWarranties();
    }
  }, [assetData?.id, activeTab]);

  useEffect(() => {
    if (assetData?.id) {
      getSubAssets();
    }
  }, [assetData?.id]);

  // Fetch all work orders eagerly when modal opens (not just when tab is active)
  useEffect(() => {
    if (isOpen && assetData?.id) {
      fetchAllWorkOrders();
    }
  }, [isOpen, assetData?.id]);

  // Fetch service requests when serviceRequests tab is active
  useEffect(() => {
    if (assetData?.id && activeTab === 'serviceRequests') {
      fetchServiceRequests();
    }
  }, [assetData?.id, activeTab, fetchServiceRequests]);

  // Debounced warranty search effect
  useEffect(() => {
    if (activeTab === 'warranties' && assetData?.id) {
      const timeoutId = setTimeout(() => {
        getWarranties();
      }, 300); // 300ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [warrantySearchTerm]);


  // When the modal opens with an initial tab hint (e.g. from the list "N Sub-Assets" chip), focus that tab.
  useEffect(() => {
    if (isOpen && initialActiveTab) {
      setActiveTab(initialActiveTab);
    }
  }, [isOpen, initialActiveTab]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('assetDetails');
      setActivities([]);
      setWarranties([]);
      setSubAssets([]);
      setAllWorkOrders([]);
      setLoadingActivities(false);
      setLoadingWarranties(false);
      setLoadingSubAssets(false);
      setLoadingWorkOrders(false);
      setFromDate(null);
      setToDate(null);
      setIsFiltering(false);
      setSearchTerm('');
      setWarrantySearchTerm('');
      setSelectedImageIndex(0);
      setShowImageModal(false);
      setWarrantyApiError('');
      setWarrantyErrors({});
      setServiceRequests([]);
      setLoadingServiceRequests(false);
      setSelectedServiceRequest(null);
      setAssetMeters([]);
      setLoadingMeters(false);
      setDetailMeterId(null);
    }
  }, [isOpen]);

  // Memoized filter work orders based on search term
  const filteredWorkOrders = useMemo(() => {
    return allWorkOrders.filter(issue =>
      (issue.ticketNumber?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (issue.asset?.assetName?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (issue.status?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (issue.workType?.toLowerCase().includes(searchTerm.toLowerCase()) || false)
    );
  }, [allWorkOrders, searchTerm]);

  // Memoized filter warranties based on search term
  const filteredWarranties = useMemo(() => {
    return warranties.filter(warranty => {
      // If no search term, show all warranties
      if (!warrantySearchTerm || warrantySearchTerm.trim() === '') {
        return true;
      }

      // Convert search term to lowercase for case-insensitive search
      const searchLower = warrantySearchTerm.toLowerCase();

      // Check various fields that might contain the search term
      const fieldsToSearch = [
        warranty?.maintenanceVendorName,
        warranty?.contractNumber,
        warranty?.notes,
        warranty?.maintenanceContractType,
        warranty?.maintenanceContractTypeDisplay,
        warranty?.overallStatus,
        warranty?.manufacturerWarrantyStatus,
        warranty?.warrantyExtensionStatus,
        warranty?.contractStatus
      ];

      // Return true if any field contains the search term
      return fieldsToSearch.some(field =>
        field && field.toString().toLowerCase().includes(searchLower)
      );
    });
  }, [warranties, warrantySearchTerm]);

  const getStatusInfo = (status) => {
    return (
      workstatusOptions(t).find(option => option.value === status) || 
      { label: status, icon: 'help-circle', colorClass: 'bg-secondary text-white', borderColor: '#6c757d' }
    );
  };

    const getStatusBadgeClass = (status) => {
    const statusClasses = {
      'PENDING': 'bg-warning',
      'APPROVED': 'bg-success',
      'REJECTED': 'bg-danger',
      'IN_PROGRESS': 'bg-primary',
      'PERMANENT_TRANSFERRED': 'bg-info',
      'TEMPORARY_TRANSFERRED': 'bg-warning',
      'PURCHASED': 'bg-success'
    };
    return statusClasses[status] || 'bg-secondary';
  };

  const getAssetStatusBadge = (status) => {
    const statusColors = {
      ONLINE: 'success',
      OFFLINE: 'danger',
      DO_NOT_TRACK: 'secondary',
      // Legacy fallbacks
      OFFLINE_PLANNED: 'danger',
      OFFLINE_UNPLANNED: 'danger',
      IN_USE: 'success',
      STANDBY: 'success',
      PLANNED_DOWNTIME: 'info',
      UNPLANNED_DOWNTIME: 'danger',
      AVAILABLE: 'secondary',
      UNAVAILABLE: 'secondary',
      DECOMMISSIONED: 'dark',
      DISPOSED: 'dark',
    };
    return statusColors[status] || 'secondary';
  };

  // Utility function to get warranty status badge
  const getWarrantyStatusBadge = (warranty) => {
    const status = warranty?.overallStatus || 'Unknown';
    const statusColors = {
      'Active': 'success',
      'Expired': 'danger',
      'Expiring Soon': 'warning',
      'Unknown': 'secondary'
    };
    return statusColors[status] || 'secondary';
  };

  // Image modal handlers
  const openImageModal = (index) => {
    setSelectedImageIndex(index);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
  };

  const nextImage = () => {
    if (assetData?.assetImageUrls?.length > 0) {
      setSelectedImageIndex((prev) => 
        prev === assetData.assetImageUrls.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (assetData?.assetImageUrls?.length > 0) {
      setSelectedImageIndex((prev) => 
        prev === 0 ? assetData.assetImageUrls.length - 1 : prev - 1
      );
    }
  };

  const contractValueFormatted = selectedWarranty?.contractValue > 0 
  ? selectedWarranty.contractValue.toLocaleString('en-US', {
      style: 'currency',
      currency: selectedWarranty?.contractCurrency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  : '';

  return (
    <AnimatePresence>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          toggle={toggle}
          size="xl"
          centered
          className="modern-modal"
          backdrop="static"
          style={{ maxHeight: '90vh' }}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="modal-content border-0 shadow-lg"
            style={{ borderRadius: '12px', overflow: 'hidden' }}
          >
            {/* Enhanced Header */}
            <div
              className="modal-header border-0 bg-gradient-primary text-white position-sticky d-flex justify-content-between align-items-center"
              style={{
                top: 0,
                zIndex: 10,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '1.5rem'
              }}
            >
              <div className="d-flex align-items-center">
                <div className="avatar-sm bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center me-3">
                  <i className="bx bx-cube text-white fs-4"></i>
                </div>
                <div>
                  <h4 className="modal-title mb-1 text-white">{assetData?.assetName || t('shared.details', 'Asset Details')}</h4>
                  <p className="mb-0 text-white-50 small">
                    <i className="bx bx-barcode me-1"></i>
                    {t('shared.asset_id', 'Asset ID')}: {assetData?.assetNumber || t('shared.not_assigned', 'Not assigned')}
                  </p>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3 ms-auto">
                {assetData?.record === false ? (
                  <Badge color="warning" className="px-3 py-2">
                      {t('shared.decommissioned','Decommissioned')}
                  </Badge>
                ) : (
                  <>
                    {assetData?.assetStatus && (
                      <Badge
                        color={getAssetStatusBadge(assetData.assetStatus)}
                        className="px-3 py-2"
                        style={{ cursor: isRequestor ? 'default' : 'pointer' }}
                        onClick={isRequestor ? undefined : () => setShowStatusModal(true)}
                        title={isRequestor ? undefined : t('shared.click_to_change_status', 'Click to change status')}
                      >
                        {assetData.assetStatus ? t(`shared.${assetData.assetStatus.toLowerCase()}`, assetData.assetStatus) : t('shared.n_a', 'N/A')}
                        {assetData.assetStatus === 'OFFLINE' && assetData.downtimeReasonName ? ` (${t(`shared.${assetData.downtimeReasonName.toLowerCase().replace(/\s+/g, '_')}`, assetData.downtimeReasonName)})` : ''}
                      </Badge>
                    )}
                    {!isRequestor && (
                      <Button
                        size="sm"
                        color="light"
                        className="text-white border-0"
                        style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                        onClick={() => setShowStatusModal(true)}
                      >
                          {t('shared.buttons_change_status', 'Change Status')}
                      </Button>
                    )}
                    {/* While the asset is promised to another organization it cannot be transferred
                        again — the badge below explains why the action is gone. */}
                    {!isRequestor && canTransferAsset && assetData?.organization?.id
                      && !assetData?.pendingCrossOrgTransfer && (
                      <Button
                        size="sm"
                        color="light"
                        className="text-white border-0"
                        style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                        onClick={() => setShowTransferModal(true)}
                      >
                          <i className="bx bx-transfer-alt me-1"></i>
                          {t('business.planning.transfer', 'Transfer')}
                      </Button>
                    )}
                    {assetData?.pendingCrossOrgTransfer && (
                      <Badge color="warning" className="align-self-center">
                        <i className="bx bx-transfer-alt me-1"></i>
                        {t('business.planning.pending_outgoing_transfer', 'Transfer pending → {{organization}}', {
                          organization: assetData.pendingCrossOrgTransfer.destinationOrganizationName || '',
                        })}
                      </Badge>
                    )}
                  </>
                )}
                {!isRequestor && onEditAsset && userData && (
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                    onClick={() => onEditAsset(userData)}
                    title={t('shared.buttons_edit_asset', 'Edit Asset')}
                    style={{
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      color: '#495057',
                      fontWeight: '500',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 1)';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.95)';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    <FeatherIcon icon="edit-3" size={16} />
                    <span>{t('shared.buttons_edit_asset', 'Edit Asset')}</span>
                  </button>
                )}
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={toggle}
                  aria-label="Close"
                ></button>
              </div>
            </div>

            {/* Decommissioned Banner */}
            {assetData?.record === false && (
              <Alert color="warning" className="mb-0 mx-4 mt-3 d-flex align-items-center gap-2" style={{ borderRadius: '8px' }}>
                <FeatherIcon icon="alert-triangle" size={18} />
                <span><strong>{t('shared.decommissioned', 'Decommissioned')}</strong> — {t('business.components.asset_modal.messages.decommissioned_message', 'This asset has been decommissioned. Data shown is read-only from the last known state.')}</span>
              </Alert>
            )}

            {/* Enhanced Navigation */}
            <Nav 
              tabs 
              className="border-bottom-0 px-4 pt-3 bg-light"
              style={{ borderRadius: '0' }}
            >
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${activeTab === 'assetDetails' ? 'active' : ''}`}
                  onClick={() => setActiveTab('assetDetails')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <i className="bx bx-info-circle me-2"></i> 
                  {t('shared.asset_details', 'Asset Details')}
                </NavLink>
              </NavItem>
              {!isRequestor && (
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'workOrders' ? 'active' : ''}`}
                    onClick={() => setActiveTab('workOrders')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="bx bx-list-ul me-2"></i>
                    {t('shared.work_orders', 'Work Orders')}
                    {allWorkOrders.length > 0 && (
                      <Badge
                        color="primary"
                        pill
                        className="ms-2"
                        style={{ fontSize: '10px' }}
                      >
                        {allWorkOrders.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
              )}
              {!isRequestor && (
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'planning' ? 'active' : ''}`}
                    onClick={() => setActiveTab('planning')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="bx bx-list-ul me-2"></i>
                    {t('shared.planning', 'Planning')}
                    {assetData?.equipmentProcesses?.length > 0 && (
                      <Badge
                        color="primary"
                        pill
                        className="ms-2"
                        style={{ fontSize: '10px' }}
                      >
                        {assetData.equipmentProcesses.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
              )}
              {!isRequestor && (
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'warranties' ? 'active' : ''}`}
                    onClick={() => setActiveTab('warranties')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="bx bx-shield-check me-2"></i>
                    {t('shared.service_contracts', 'Service Contracts')}
                    {warranties.length > 0 && (
                      <Badge
                        color="success"
                        pill
                        className="ms-2"
                        style={{ fontSize: '10px' }}
                      >
                        {warranties.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
              )}
              {!isRequestor && (
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'tco' ? 'active' : ''}`}
                    onClick={() => setActiveTab('tco')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="bx bx-pie-chart-alt-2 me-2"></i>
                    {t('cost_tracking.asset_tco.tab_label', 'TCO')}
                  </NavLink>
                </NavItem>
              )}
              {!isRequestor && (
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'subAssets' ? 'active' : ''}`}
                    onClick={() => setActiveTab('subAssets')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="bx bx-sitemap me-2"></i>
                    {t('shared.sub_assets', 'Sub Assets')}
                    {subAssets.length > 0 && (
                      <Badge
                        color="info"
                        pill
                        className="ms-2"
                        style={{ fontSize: '10px' }}
                      >
                        {subAssets.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
              )}
              {!isRequestor && (
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'activities' ? 'active' : ''}`}
                    onClick={() => setActiveTab('activities')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <FeatherIcon icon="activity" size={16} className="me-2" />
                    {t('shared.activity_log', 'Activity Log')}
                    {activities.length > 0 && (
                      <Badge
                        color="secondary"
                        pill
                        className="ms-2"
                        style={{ fontSize: '10px' }}
                      >
                        {activities.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
              )}
              {!isRequestor && (
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'spareParts' ? 'active' : ''}`}
                    onClick={() => setActiveTab('spareParts')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <FeatherIcon icon="tool" size={16} className="me-2" />
                    {t('shared.spare_parts', 'Spare Parts')}
                    {sparePartsUsage.length > 0 && (
                      <Badge color="primary" pill className="ms-2" style={{ fontSize: '10px' }}>
                        {sparePartsUsage.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
              )}
              {!isRequestor && (
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'meters' ? 'active' : ''}`}
                    onClick={() => setActiveTab('meters')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <FeatherIcon icon="activity" size={16} className="me-2" />
                    {t('shared.meters', 'Meters')}
                    {assetMeters.length > 0 && (
                      <Badge color="primary" pill className="ms-2" style={{ fontSize: '10px' }}>
                        {assetMeters.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
              )}
              {isRequestor && (
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'serviceRequests' ? 'active' : ''}`}
                    onClick={() => setActiveTab('serviceRequests')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <FeatherIcon icon="clipboard" size={16} className="me-2" />
                    {t('shared.service_requests', 'Service Requests')}
                    {serviceRequests.length > 0 && (
                      <Badge color="primary" pill className="ms-2" style={{ fontSize: '10px' }}>
                        {serviceRequests.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
              )}
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'documents' ? 'active' : ''}`}
                    onClick={() => setActiveTab('documents')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <FeatherIcon icon="file-text" size={16} className="me-2" />
                    {t('shared.documents', 'Documents')}
                    {assetData?.documentUrls?.length > 0 && (
                      <Badge color="info" pill className="ms-2" style={{ fontSize: '10px' }}>
                        {assetData.documentUrls.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
            </Nav>

            {/* Modal Body */}
            <div 
              className="modal-body p-0" 
              style={{ 
                maxHeight: 'calc(90vh - 200px)',
                overflow: 'auto'
              }}
            >
              <TabContent activeTab={activeTab} className="h-100">
                {/* Asset Details Tab */}
                <TabPane tabId="assetDetails">
                  <div className="p-4">
                    <Card className="border-0 shadow-sm">
                      <CardBody className="p-4">
                        <div className="d-flex align-items-center mb-4">
                          <div className="info-icon me-3">
                            <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                 style={{ width: '40px', height: '40px' }}>
                              <i className="bx bx-cube text-primary fs-5"></i>
                            </div>
                          </div>
                          <div>
                            <h5 className="mb-1 fw-bold">{t('shared.asset_information', 'Asset Information')}</h5>
                            <p className="text-muted small mb-0">{t('shared.complete_asset_specifications_and_detail', 'Complete asset specifications and details')}</p>
                          </div>
                        </div>

                        {loading ? (
                          <div className="text-center py-5">
                            <Spinner color="primary" size="lg" />
                            <p className="text-muted mt-3">{t('business.components.asset_modal.messages.loading_asset_details', 'Loading asset details...')}</p>
                          </div>
                        ) : (
                          <Row className="g-4">
                            {/* Basic Information */}
                            <Col md={6}>
                              <div className="info-card bg-light rounded p-3 h-100">
                                <h6 className="text-primary mb-3 fw-semibold">
                                  <i className="bx bx-info-circle me-2"></i>{t('shared.basic_information', 'Basic Information')}
                                </h6>
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.asset_name', 'Asset Name')}</label>
                                  <div className="fw-medium">{assetData?.assetName || t('shared.n_a', 'N/A')}</div>
                                </div>
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.asset_id', 'Asset ID')}</label>
                                  <div className="fw-medium">{assetData?.assetNumber || t('shared.n_a', 'N/A')}</div>
                                </div>
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.serial_number', 'Serial Number')}</label>
                                  <div className="fw-medium">{assetData?.serialNumber || t('shared.n_a', 'N/A')}</div>
                                </div>
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.model_number', 'Model Number')}</label>
                                  <div className="fw-medium">{assetData?.modelNumber || t('shared.n_a', 'N/A')}</div>
                                </div>
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.status', 'Status')}</label>
                                  <div>
                                    <Badge color={getAssetStatusBadge(assetData?.assetStatus)} className="px-2 py-1">
                                      {assetData?.assetStatus ? t(`shared.${assetData?.assetStatus.toLowerCase()}`, assetData?.assetStatus) : t('shared.n_a', 'N/A')}
                                      {assetData?.assetStatus === 'OFFLINE' && assetData?.downtimeReasonName ? ` (${t(`shared.${assetData.downtimeReasonName.toLowerCase().replace(/\s+/g, '_')}`, assetData.downtimeReasonName)})` : ''}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                            </Col>


                            {/* Other Details */}
                            <Col md={6}>
                              <div className="info-card bg-light rounded p-3 h-100">
                                <h6 className="text-success mb-3 fw-semibold">
                                  <i className="bx bx-cog me-2"></i>{t('shared.other_details', 'Other Details')}
                                </h6>
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.manufacturer', 'Manufacturer')}</label>
                                  <div className="fw-medium">{assetData?.manufacturer || t('shared.n_a', 'N/A')}</div>
                                </div>
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.asset_types', 'Asset Types')}</label>
                                  <div className="fw-medium">{assetData?.assetTypes?.map(at => at.name).join(', ') || t('shared.n_a', 'N/A')}</div>
                                </div>
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.location', 'Location')}</label>
                                  <div className="fw-medium">
                                    {assetData?.parentLocationName
                                      ? `${assetData.parentLocationName} → ${assetData.currentLocation}`
                                      : (assetData?.currentLocation || t('shared.n_a', 'N/A'))}
                                  </div>
                                </div>
                                {assetData?.assetStatus === 'OFFLINE' && assetData?.downtimeReasonName && (
                                  <div className="info-item mb-3">
                                    <label className="text-muted small">{t('shared.downtime_reason', 'Downtime Reason')}</label>
                                    <div className="fw-medium">{t(`shared.${assetData?.downtimeReasonName.toLowerCase().replace(/\s+/g, '_')}`, assetData?.downtimeReasonName)}</div>
                                  </div>
                                )}
                                {assetData?.assetStatus === 'DECOMMISSIONED' && (
                                  <>
                                    {assetData?.decommissionedReason && (
                                      <div className="info-item mb-3">
                                        <label className="text-muted small">{t('business.assets.decommission_reason', 'Decommission reason')}</label>
                                        <div className="fw-medium">{assetData.decommissionedReason}</div>
                                      </div>
                                    )}
                                    {assetData?.decommissionedOn && (
                                      <div className="info-item mb-3">
                                        <label className="text-muted small">{t('business.assets.decommissioned_on', 'Decommissioned on')}</label>
                                        <div className="fw-medium">{assetData.decommissionedOn}</div>
                                      </div>
                                    )}
                                  </>
                                )}
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.vendor_name', 'Vendor Name')}</label>
                                  <div className="fw-medium">{assetData?.vendorName || t('shared.n_a', 'N/A')}</div>
                                </div>
                                <div className="info-item mb-0">
                                  <label className="text-muted small">{t('shared.criticality', 'Criticality')}</label>
                                  <div className="fw-medium">{assetData?.criticality || t('shared.n_a', 'N/A')}</div>
                                </div>
                              </div>
                            </Col>

                            {/* Acquisition & Financial Information - Hidden for requesters */}
                            {!isRequestor && <Col md={6}>
                              <div className="info-card bg-light rounded p-3 h-100">
                                <h6 className="text-success mb-3 fw-semibold">
                                  <i className="bx bx-dollar-circle me-2"></i>
                                  {assetData?.acquisitionType && assetData.acquisitionType !== 'PURCHASED'
                                    ? t('business.assets.acquisition_information', 'Acquisition Information')
                                    : t('shared.financial_information', 'Financial Information')}
                                </h6>
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('business.assets.acquisition_type', 'Acquisition Type')}</label>
                                  <div className="fw-medium">
                                    <Badge color={
                                      { 'PURCHASED': 'success', 'RENTAL': 'info', 'DEMO': 'warning', 'PLACEMENT': 'primary', 'DONATION': 'secondary' }[assetData?.acquisitionType] || 'success'
                                    } className="px-2 py-1">
                                      {{ 'PURCHASED': t('business.assets.purchased', 'Purchased'),
                                         'RENTAL': t('business.assets.rental', 'Rental'),
                                         'DEMO': t('business.assets.demo', 'Demo'),
                                         'PLACEMENT': t('business.assets.placement', 'Placement'),
                                         'DONATION': t('business.assets.donation', 'Donation/Grant'),
                                      }[assetData?.acquisitionType] || t('business.assets.purchased', 'Purchased')}
                                    </Badge>
                                  </div>
                                </div>
                                {['RENTAL', 'DEMO', 'PLACEMENT'].includes(assetData?.acquisitionType) && (<>
                                  <div className="info-item mb-3">
                                    <label className="text-muted small">
                                      {{ 'RENTAL': t('business.assets.rental_start_date', 'Rental Start Date'),
                                         'DEMO': t('business.assets.demo_start_date', 'Demo Start Date'),
                                         'PLACEMENT': t('business.assets.placement_start_date', 'Placement Start Date'),
                                      }[assetData.acquisitionType]}
                                    </label>
                                    <div className="fw-medium">{assetData?.acquisitionStartDate || t('shared.n_a', 'N/A')}</div>
                                  </div>
                                  <div className="info-item mb-0">
                                    <label className="text-muted small">
                                      {{ 'RENTAL': t('business.assets.rental_end_date', 'Rental End Date'),
                                         'DEMO': t('business.assets.demo_end_date', 'Demo End Date'),
                                         'PLACEMENT': t('business.assets.placement_end_date', 'Placement End Date'),
                                      }[assetData.acquisitionType]}
                                    </label>
                                    <div className="fw-medium">{assetData?.acquisitionEndDate || t('shared.n_a', 'N/A')}</div>
                                  </div>
                                </>)}
                                {(!assetData?.acquisitionType || assetData?.acquisitionType === 'PURCHASED') && (<>
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.original_cost', 'Original Cost')}</label>
                                  <div className="fw-medium">
                                    {assetData?.assetCost ?
                                      assetData.assetCost.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: assetData?.organization?.currency || assetData?.currency || 'USD',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                      })
                                      : t('shared.n_a', 'N/A')
                                    }
                                  </div>
                                </div>
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.currency', 'Currency')}</label>
                                  <div className="fw-medium">{assetData?.organization?.currency || assetData?.currency || t('shared.n_a', 'N/A')}</div>
                                </div>
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.year_of_purchase', 'Year of Purchase')}</label>
                                  <div className="fw-medium">{assetData?.yearOfPurchase || t('shared.n_a', 'N/A')}</div>
                                </div>
                                <div className="info-item mb-0">
                                  <label className="text-muted small">{t('shared.commissioning_date', 'Commissioning Date')}</label>
                                  <div className="fw-medium">
                                    {assetData?.commissioningDate ? (() => {
                                      if (assetData.commissioningDate.includes('-') && assetData.commissioningDate.split('-').length === 3) {
                                        const parts = assetData.commissioningDate.split('-');
                                        if (parts[0].length === 2) {
                                          return assetData.commissioningDate;
                                        } else {
                                          return `${parts[2]}-${parts[1]}-${parts[0]}`;
                                        }
                                      }
                                      return assetData.commissioningDate;
                                    })() : t('shared.n_a', 'N/A')}
                                  </div>
                                </div>
                                </>)}
                              </div>
                            </Col>}

                            {/* Depreciation - Only for PURCHASED assets, hidden for requesters */}
                            {!isRequestor && (!assetData?.acquisitionType || assetData?.acquisitionType === 'PURCHASED') && <Col md={6}>
                              <div className="info-card bg-light rounded p-3 h-100">
                                <h6 className="text-success mb-3 fw-semibold">
                                  <i className="bx bx-trending-down me-2"></i>{t('shared.depreciation', 'Depreciation')}
                                </h6>
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.estimated_lifespan', 'Estimated Lifespan')}</label>
                                  <div className="fw-medium">
                                    {assetData?.estimatedLifespan ? `${assetData.estimatedLifespan} ${t('shared.years', 'years')}` : t('shared.n_a', 'N/A')}
                                  </div>
                                </div>
                                {assetData?.depreciationPeriodYears && (
                                  <div className="info-item mb-3">
                                    <label className="text-muted small">{t('shared.accelerated_depreciation', 'Accelerated Depreciation')}</label>
                                    <div className="fw-medium">{assetData.depreciationPeriodYears} {t('shared.years', 'years')}</div>
                                  </div>
                                )}
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.depreciation_rate', 'Depreciation Rate')}</label>
                                  <div className="fw-medium">
                                    {assetData?.depreciationRate ? `${assetData.depreciationRate}%` : t('shared.n_a', 'N/A')}
                                  </div>
                                </div>
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.annual_depreciation', 'Annual Depreciation')}</label>
                                  <div className="fw-medium">
                                    {assetData?.annualDepreciation ?
                                      assetData.annualDepreciation.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: assetData?.organization?.currency || assetData?.currency || 'USD',
                                      })
                                      : t('shared.n_a', 'N/A')
                                    }
                                  </div>
                                </div>
                                <div className="info-item mb-3">
                                  <label className="text-muted small">{t('shared.accumulated_depreciation', 'Accumulated Depreciation')}</label>
                                  <div className="fw-medium">
                                    {assetData?.accumulatedDepreciation ?
                                      assetData.accumulatedDepreciation.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: assetData?.organization?.currency || assetData?.currency || 'USD',
                                      })
                                      : t('shared.n_a', 'N/A')
                                    }
                                  </div>
                                </div>
                                <div className="info-item mb-0">
                                  <label className="text-muted small">{t('shared.net_book_value', 'Net Book Value')}</label>
                                  <div className="fw-medium">
                                    {assetData?.netBookValue ?
                                      assetData.netBookValue.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: assetData?.organization?.currency || assetData?.currency || 'USD',
                                      })
                                      : t('shared.n_a', 'N/A')
                                    }
                                  </div>
                                </div>
                              </div>
                            </Col>}

                            {/* Downtime Cost Estimates - Hidden for requesters */}
                            {!isRequestor && (
                              <Col md={6}>
                                <div className="info-card bg-light rounded p-3 h-100">
                                  <h6 className="text-success mb-3 fw-semibold">
                                    <i className="bx bx-time-five me-2"></i>
                                    {t('business.assets.downtime_cost_estimates', 'Downtime Cost Estimates')}
                                  </h6>
                                  <div className="info-item mb-3">
                                    <label className="text-muted small">
                                      {t('business.assets.average_price_per_case', 'Average price by case')}
                                    </label>
                                    <div className="fw-medium">
                                      {assetData?.averagePricePerCase
                                        ? Number(assetData.averagePricePerCase).toLocaleString('en-US', {
                                            style: 'currency',
                                            currency: assetData?.organization?.currency || assetData?.currency || 'USD',
                                          })
                                        : '\u2014'}
                                    </div>
                                  </div>
                                  <div className="info-item mb-0">
                                    <label className="text-muted small">
                                      {t('business.assets.average_duration_per_case', 'Average duration by case (hours)')}
                                    </label>
                                    <div className="fw-medium">
                                      {assetData?.averageDurationPerCaseHours
                                        ? `${assetData.averageDurationPerCaseHours} ${t('shared.hours_abbr', 'h')}`
                                        : '\u2014'}
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            )}

                            {/* Custom Fields - hidden for requestors */}
                            {!isRequestor && Array.isArray(assetData?.customFields) && assetData.customFields.length > 0 && (
                              <Col md={6}>
                                <div className="info-card bg-light rounded p-3 h-100">
                                  <h6 className="mb-3 fw-semibold" style={{ color: '#7c3aed' }}>
                                    <i className="bx bx-data me-2"></i>{t('shared.custom_fields', 'Custom Fields')}
                                  </h6>
                                  {assetData.customFields.map((field, index) => {
                                    const renderValue = () => {
                                      const v = field.value;
                                      if (v == null || v === '') return t('shared.n_a', 'N/A');
                                      switch (field.fieldType) {
                                        case 'CHECKBOX':
                                          return v === 'true' ? t('shared.yes', 'Yes') : t('shared.no', 'No');
                                        case 'DATE': {
                                          const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
                                          return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
                                        }
                                        case 'DATETIME': {
                                          const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(v);
                                          return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : v;
                                        }
                                        case 'MULTISELECT': {
                                          try {
                                            const arr = JSON.parse(v);
                                            return Array.isArray(arr) && arr.length ? arr.join(', ') : t('shared.n_a', 'N/A');
                                          } catch { return String(v); }
                                        }
                                        case 'LINK': {
                                          const href = v.startsWith('tel:') || /^https?:\/\//i.test(v) ? v : `https://${v}`;
                                          return <a href={href} target="_blank" rel="noopener noreferrer">{v}</a>;
                                        }
                                        default:
                                          return String(v);
                                      }
                                    };
                                    return (
                                      <div key={field.definitionId ?? index} className={`info-item ${index < assetData.customFields.length - 1 ? 'mb-3' : 'mb-0'}`}>
                                        <label className="text-muted small">{field.label}</label>
                                        <div className="fw-medium">{renderValue()}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </Col>
                            )}

                            {/* Description */}
                            <Col md={12}>
                              <div className="info-card bg-light rounded p-3">
                                <h6 className="text-warning mb-3 fw-semibold">
                                  <i className="bx bx-file-blank me-2"></i>{t('shared.description', 'Description')}
                                </h6>
                                <div className="info-item mb-0">
                                  <div className="text-wrap" style={{ lineHeight: '1.5' }}>
                                    {assetData?.description || t('shared.no_description_available', 'No description available')}
                                  </div>
                                </div>
                              </div>
                            </Col>

                            {/* Asset Images Section */}
                            {assetData?.assetImageUrls && assetData.assetImageUrls.length > 0 && (
                              <Col md={12}>
                                <div className="info-card bg-light rounded p-3">
                                  <h6 className="text-primary mb-3 fw-semibold">
                                    <i className="bx bx-image me-2"></i>{t('shared.asset_images', 'Asset Images')}
                                    <Badge color="primary" className="ms-2" style={{ fontSize: '10px' }}>
                                      {assetData.assetImageUrls.length}
                                    </Badge>
                                  </h6>
                                  <div className="row g-2">
                                    {assetData.assetImageUrls.map((imageUrl, index) => (
                                      <div key={index} className="col-md-3 col-sm-4 col-6">
                                        <motion.div
                                          className="image-thumbnail position-relative"
                                          whileHover={{ scale: 1.05 }}
                                          whileTap={{ scale: 0.95 }}
                                          onClick={() => openImageModal(index)}
                                          style={{ cursor: 'pointer' }}
                                        >
                                          <img
                                            src={imageUrl}
                                            alt={`Asset image ${index + 1}`}
                                            className="img-fluid rounded"
                                            style={{
                                              width: '100%',
                                              height: '120px',
                                              objectFit: 'cover',
                                              border: '2px solid #dee2e6',
                                              transition: 'all 0.3s ease'
                                            }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                          <div className="image-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded"
                                               style={{
                                                 backgroundColor: 'rgba(0,0,0,0.5)',
                                                 opacity: 0,
                                                 transition: 'opacity 0.3s ease'
                                               }}
                                               onMouseEnter={(e) => e.target.style.opacity = 1}
                                               onMouseLeave={(e) => e.target.style.opacity = 0}
                                          >
                                            <i className="bx bx-search-alt text-white fs-4"></i>
                                          </div>
                                        </motion.div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </Col>
                            )}

                            {/* QR Code and NFC Tag Section */}
                            {(assetData?.qrImageUrl || assetData?.uuid) && (
                              <Col md={12}>
                                <div className="info-card">
                                  <Row className="g-3">
                                    {/* QR Code */}
                                    {assetData?.qrImageUrl && (
                                      <Col md={assetData?.uuid ? 6 : 12}>
                                        <div className="bg-light rounded p-3 h-100">
                                        <h6 className="text-secondary mb-3 fw-semibold">
                                          <i className="bx bx-qr-scan me-2"></i>{t('shared.qr_code', 'QR Code')}
                                        </h6>
                                        <div className="text-center">
                                          <img
                                            src={assetData.qrImageUrl}
                                            alt="Asset QR Code"
                                            className="img-fluid"
                                            style={{
                                              maxWidth: '150px',
                                              border: '2px solid #dee2e6',
                                              borderRadius: '8px'
                                            }}
                                          />
                                          <div className="mt-2">
                                            <small className="text-muted">{t('shared.scan_to_view_asset_details', 'Scan to view asset details')}</small>
                                          </div>
                                        </div>
                                        </div>
                                      </Col>
                                    )}

                                    {/* NFC Tag */}
                                    {assetData?.uuid && (
                                      <Col md={assetData?.qrImageUrl ? 6 : 12}>
                                        <div className="bg-light rounded p-3 h-100">
                                        <h6 className="text-secondary mb-3 fw-semibold">
                                          <i className="bx bx-chip me-2"></i>{t('shared.nfc_tag', 'NFC Tag')}
                                        </h6>
                                        <div className="text-center">
                                          <div
                                            className="d-inline-flex align-items-center justify-content-center mb-2"
                                            style={{
                                              width: '80px',
                                              height: '80px',
                                              backgroundColor: '#e8f4fd',
                                              borderRadius: '12px',
                                              border: '2px solid #dee2e6',
                                            }}
                                          >
                                            <i className="bx bx-wifi" style={{ fontSize: '40px', color: '#0d6efd', transform: 'rotate(45deg)' }}></i>
                                          </div>
                                          <div className="mt-2">
                                            <code style={{ fontSize: '0.75rem', color: '#d63384', wordBreak: 'break-all' }}>
                                              {assetData.uuid}
                                            </code>
                                          </div>
                                          <div className="mt-1">
                                            <small className="text-muted">{t('shared.tap_nfc_tag_to_view_asset', 'Tap NFC tag to view asset')}</small>
                                          </div>
                                        </div>
                                        </div>
                                      </Col>
                                    )}
                                  </Row>
                                </div>
                              </Col>
                            )}
                          </Row>
                        )}
                      </CardBody>
                    </Card>
                  </div>
                </TabPane>

                {/* Work Orders Tab */}
                <TabPane tabId="workOrders">
                  <div className="p-4">
                    {/* Enhanced Filter Section */}
                    <Card className="border-0 shadow-sm mb-4">
                      <CardBody className="p-3">
                        <div className="d-flex align-items-center mb-3">
                          <i className="bx bx-filter-alt text-primary me-2 fs-5"></i>
                          <h6 className="mb-0 text-muted">{t('shared.filters_and_search', 'Filters & Search')}</h6>
                        </div>
                        
                        <Row className="g-3">
                          {/* Search Bar */}
                          <Col md={4}>
                            <InputGroup>
                              <span className="input-group-text bg-transparent border-end-0">
                                <i className="bx bx-search text-muted"></i>
                              </span>
                              <Input
                                type="text"
                                placeholder={t('shared.search_tickets_assets_status', 'Search tickets, assets, status...')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="border-start-0 ps-0"
                              />
                            </InputGroup>
                          </Col>

                          {/* From Date Picker */}
                          <Col md={3}>
                            <div className="position-relative">
                              <DatePicker
                                selected={fromDate}
                                onChange={(date) => {
                                  if (date) {
                                    const utcDate = new Date(Date.UTC(
                                      date.getFullYear(),
                                      date.getMonth(),
                                      date.getDate(),
                                      0, 0, 0
                                    ));
                                    setFromDate(utcDate);
                                  } else {
                                    setFromDate(null);
                                  }
                                }}
                                selectsStart
                                startDate={fromDate}
                                endDate={toDate}
                                className="form-control"
                                placeholderText={t('shared.from_date', 'From date')}
                                popperProps={{ style: { zIndex: 100 } }}
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                              />
                              <i className="bx bx-calendar position-absolute top-50 end-0 translate-middle-y me-3 text-muted"></i>
                            </div>
                          </Col>

                          {/* To Date Picker */}
                          <Col md={3}>
                            <div className="position-relative">
                              <DatePicker
                                selected={toDate}
                                onChange={(date) => {
                                  if (date) {
                                    const utcDate = new Date(Date.UTC(
                                      date.getFullYear(),
                                      date.getMonth(),
                                      date.getDate(),
                                      23, 59, 59
                                    ));
                                    setToDate(utcDate);
                                  } else {
                                    setToDate(null);
                                  }
                                }}
                                selectsEnd
                                startDate={fromDate}
                                endDate={toDate}
                                minDate={fromDate}
                                className="form-control"
                                placeholderText={t('shared.to_date', 'To date')}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="MMMM d, yyyy h:mm aa"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                              />
                              <i className="bx bx-calendar position-absolute top-50 end-0 translate-middle-y me-3 text-muted"></i>
                            </div>
                          </Col>
                          <Col md={2} className="d-flex gap-2">
                            <Button 
                              color="primary" 
                              size="sm"
                              onClick={applyDateFilters}
                              disabled={isFiltering || (!fromDate && !toDate)}
                              className="flex-fill"
                            >
                              {isFiltering ? (
                                <Spinner size="sm" className="me-1" />
                              ) : (
                                <i className="bx bx-check me-1"></i>
                              )}
                              {t('shared.apply', 'Apply')}
                            </Button>
                            <Button 
                              color="outline-secondary" 
                              size="sm"
                              onClick={clearDateFilters}
                              disabled={isFiltering || (!fromDate && !toDate && !searchTerm)}
                            >
                              <i className="bx bx-x"></i>
                            </Button>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    {/* Work Orders Table */}
                    <div style={{ maxHeight: 'calc(90vh - 400px)', overflow: 'auto' }}>
                      {loadingWorkOrders || isFiltering ? (
                        <div className="text-center py-5">
                          <Spinner color="primary" size="lg" />
                          <p className="text-muted mt-3">
                            {isFiltering ? 'Applying filters...' : 'Loading work orders...'}
                          </p>
                        </div>
                      ) : filteredWorkOrders.length > 0 ? (
                        <div className="table-responsive">
                          <Table hover className="mb-0 modern-table">
                            <thead className="table-light position-sticky top-0" style={{ zIndex: 0 }}>
                              <tr>
                                <th className="border-0 py-3">
                                  <i className="bx bx-hash me-1 text-muted"></i>
                                  {t('shared.ticket', 'Ticket')}
                                </th>
                                 <th className="border-0 py-3">
                                  <i className="bx bx-hash me-1 text-muted"></i>
                                  {t('shared.type', 'Type')}
                                </th>
                                <th className="border-0 py-3">
                                  <i className="bx bx-calendar me-1 text-muted"></i>
                                  {t('shared.created_at', 'Created At')}
                                </th>
                                  <th className="border-0 py-3">
                                  <i className="bx bx-calendar me-1 text-muted"></i>
                                  {t('shared.due_at', 'Due At')}
                                </th>
                                <th className="border-0 py-3">
                                  <i className="bx bx-package me-1 text-muted"></i>
                                  {t('shared.asset', 'Asset')}
                                </th>
                                <th className="border-0 py-3">
                                  <i className="bx bx-flag me-1 text-muted"></i>
                                  {t('shared.status', 'Status')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <AnimatePresence>
                                {filteredWorkOrders.map((issue, index) => {
                                  const statusInfo = getStatusInfo(issue.status);
                                  return (
                                    <motion.tr
                                      key={issue.id || index}
                                      custom={index}
                                      variants={tableRowVariants}
                                      initial="hidden"
                                      animate="visible"
                                      exit="hidden"
                                      whileHover={{
                                        backgroundColor: '#f8f9fa',
                                        transition: { duration: 0.2 }
                                      }}
                                      className="cursor-pointer"
                                    >
                                      <td className="py-3">
                                        <div className="d-flex align-items-center">
                                          <div
                                            className="ticket-number-badge"
                                            onClick={() => {
                                              if (issue.workType === 'Preventive') {
                                                handlePreventiveWorkOrderClick(issue);
                                              } else if (onTicketClick) {
                                                onTicketClick(issue.ticketNumber);
                                              }
                                            }}
                                            style={{
                                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                              color: 'white',
                                              padding: '4px 12px',
                                              borderRadius: '20px',
                                              fontSize: '12px',
                                              fontWeight: '600',
                                              cursor: 'pointer',
                                              transition: 'transform 0.2s ease',
                                            }}
                                          >
                                            {issue.ticketNumber}
                                          </div>
                                        </div>
                                      </td>
                                       <td className="py-3">
                                        <Badge
                                          style={{
                                            backgroundColor:
                                              issue?.workType === 'Reactive' ? '#4285f4' :
                                              issue?.workType === 'Calibration' ? '#ea4335' :
                                              issue?.workType === 'Testing' ? '#fbbc04' :
                                              issue?.workType === 'Routine' ? '#34a853' :
                                              issue?.workType === 'Recall' ? '#9334e6' :
                                              issue?.workType === 'Preventive' ? '#ff6d00' : '#5f6368',
                                            color: 'white',
                                            fontSize: '11px',
                                            padding: '4px 10px',
                                            fontWeight: '600',
                                            borderRadius: '12px'
                                          }}
                                        >
                                            {issue?.workType ? t(`shared.work_types.${issue.workType.toLowerCase()}`, issue.workType) : t('shared.n_a', 'N/A')}
                                          </Badge>
                                      </td>
                                      <td className="py-3 text-muted">
                                        {formatCreatedDate(issue.createdAt)}
                                      </td>
                                       <td className="py-3 text-muted">
                                        {formatCreatedDate(issue.requestDate)}
                                      </td>
                                      <td className="py-3">
                                        <div className="d-flex align-items-center">
                                          <div className="avatar-xs bg-light rounded me-2 d-flex align-items-center justify-content-center">
                                            <i className="bx bx-package text-primary"></i>
                                          </div>
                                          <span className="fw-medium">
                                            {issue.asset?.assetName || t('shared.n_a', 'N/A')}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="py-3">
                                        <Badge
                                          className={`d-inline-flex align-items-center ${statusInfo.colorClass}`}
                                          style={{
                                            padding: '6px 12px',
                                            borderRadius: '20px',
                                            fontSize: '11px',
                                            fontWeight: '500',
                                            border: `1px solid ${statusInfo.borderColor}`
                                          }}
                                        >
                                          <FeatherIcon icon={statusInfo.icon} size={12} className="me-1" />
                                          {t(`shared.${issue?.status.toLowerCase().replace(/\s+/g, '_')}`, formatEnumName(issue?.status))}
                                        </Badge>
                                      </td>
                                    </motion.tr>
                                  );
                                })}
                              </AnimatePresence>
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <div className="avatar-lg bg-light rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                            <i className="bx bx-search text-muted fs-1"></i>
                          </div>
                          <h5 className="text-muted">{t('shared.no_work_orders_found', 'No work orders found')}</h5>
                          <p className="text-muted mb-0">
                            {searchTerm || fromDate || toDate 
                              ? t('shared.try_adjusting_filters', 'Try adjusting your filters or search terms')
                              : t('shared.this_asset_has_no_reported_work_orders', 'This asset has no reported work orders')
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </TabPane>


                      {/* Planning */}
                <TabPane tabId="planning">
                  <div className="p-4">
                    <Card className="border-0 shadow-sm">
                      <CardBody className="p-4">
                        {/* Header Section */}
                        <div className="d-flex align-items-center justify-content-between mb-4">
                          <div className="d-flex align-items-center">
                            <div className="info-icon me-3">
                              <div className="bg-success bg-opacity-50 rounded-circle d-flex align-items-center justify-content-center"
                                   style={{ width: '40px', height: '40px' }}>
                                <i className="bx bx-shield-check text-success fs-5"></i>
                              </div>
                            </div>
                            <div>
                              <h5 className="mb-1 fw-bold">{t('shared.planning', 'Planning')}</h5>
                              <p className="text-muted small mb-0">{t('shared.manage_planning_requests', 'Manage Planning Requests')}</p>
                            </div>
                          </div>
                        </div>

                        {/* Acquisition Info Banner */}
                        {['RENTAL', 'DEMO', 'PLACEMENT'].includes(assetData?.acquisitionType) && (() => {
                          const endDate = assetData?.acquisitionEndDate;
                          let daysRemaining = null;
                          let bannerColor = 'info';
                          if (endDate) {
                            const parts = endDate.split('-');
                            const end = parts[0].length === 2
                              ? new Date(parts[2], parts[1] - 1, parts[0])
                              : new Date(parts[0], parts[1] - 1, parts[2]);
                            daysRemaining = Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
                            if (daysRemaining < 0) bannerColor = 'danger';
                            else if (daysRemaining <= 30) bannerColor = 'warning';
                          }
                          const typeLabel = {
                            'RENTAL': t('business.assets.rental', 'Rental'),
                            'DEMO': t('business.assets.demo', 'Demo'),
                            'PLACEMENT': t('business.assets.placement', 'Placement'),
                          }[assetData.acquisitionType];
                          return (
                            <Alert color={bannerColor} className="mb-4 d-flex align-items-center justify-content-between">
                              <div>
                                <i className="bx bx-info-circle me-2"></i>
                                <strong>{typeLabel}</strong>
                                {assetData?.acquisitionStartDate && <span className="ms-2">{t('shared.from', 'From')}: {assetData.acquisitionStartDate}</span>}
                                {assetData?.acquisitionEndDate && <span className="ms-2">{t('shared.to', 'To')}: {assetData.acquisitionEndDate}</span>}
                              </div>
                              {daysRemaining !== null && (
                                <Badge color={bannerColor === 'info' ? 'info' : bannerColor} className="ms-2">
                                  {daysRemaining < 0
                                    ? t('business.assets.overdue_days', '{{count}} days overdue', { count: Math.abs(daysRemaining) })
                                    : t('business.assets.days_remaining', '{{count}} days remaining', { count: daysRemaining })}
                                </Badge>
                              )}
                            </Alert>
                          );
                        })()}

                        {/* Equipment Processes Content */}
                       <div style={{ maxHeight: 'calc(90vh - 400px)', overflow: 'auto' }}>
                      {assetData?.equipmentProcesses?.length > 0 ? (
                        <div className="table-responsive">
                          <Table hover className="mb-0 modern-table">
                            <thead className="table-light position-sticky top-0" style={{ zIndex: 0 }}>
                            <tr>
                              <th className="border-0 py-3">{t('shared.request_id', 'Request ID')}</th>
                              <th className="border-0 py-3">{t('shared.start_date', 'Start Date')}</th>
                              <th className="border-0 py-3">{t('shared.end_date', 'End Date')}</th>
                              <th className="border-0 py-3">{t('shared.location', 'Location')}</th>
                              <th className="border-0 py-3">{t('shared.status', 'Status')}</th>
                              <th className="border-0 py-3">{t('shared.reason', 'Reason')}</th>
                            </tr>
                            </thead>
                            <tbody>
                              <AnimatePresence>
                                {assetData?.equipmentProcesses?.map((issue, index) => {
                                  const statusInfo = getStatusInfo(issue.status);
                                  return (
                                    <motion.tr
                                      key={issue.id || index}
                                      custom={index}
                                      variants={tableRowVariants}
                                      initial="hidden"
                                      animate="visible"
                                      exit="hidden"
                                      whileHover={{
                                        backgroundColor: '#f8f9fa',
                                        transition: { duration: 0.2 }
                                      }}
                                      className="cursor-pointer"
                                      onClick={() => openTransferDetails(issue)}
                                    >
                                      <td className="py-3">
                                        {transferByProcess[issue.id]?.requestId ? (
                                          <span className="text-primary fw-medium" role="button" style={{ cursor: 'pointer' }}
                                            onClick={(e) => { e.stopPropagation(); openTransferDetails(issue); }}>
                                            {transferByProcess[issue.id].requestId}
                                          </span>
                                        ) : (
                                          <span className="text-muted">{index + 1}</span>
                                        )}
                                      </td>
                                      <td className="py-3 text-muted">
                                        {formatCreatedDate(issue.startDate)}
                                      </td>
                                       <td className="py-3 text-muted">
                                        {formatCreatedDate(issue.endDate)}
                                      </td>
                                      <td className="py-3">
                                        {issue.fromLocationsInfo?.name && (
                                          <>
                                            <span className="text-primary" role="button" style={{ cursor: 'pointer' }}
                                              onClick={(e) => { e.stopPropagation(); openPlanningLocation(issue.fromLocationsInfo.name); }}>
                                              {issue.fromLocationsInfo.name}
                                            </span>
                                            {' → '}
                                          </>
                                        )}
                                        {issue.locationsInfo?.name ? (
                                          <span className="text-primary" role="button" style={{ cursor: 'pointer' }}
                                            onClick={(e) => { e.stopPropagation(); openPlanningLocation(issue.locationsInfo.name); }}>
                                            {issue.locationsInfo.name}
                                          </span>
                                        ) : t('shared.n_a', 'N/A')}
                                      </td>
                                      <td className="py-3">
                                        <Badge
                                          className={`badge ${getStatusBadgeClass(issue?.status)} rounded-pill px-3 py-1`}
                                          style={{
                                            padding: '6px 12px',
                                            borderRadius: '20px',
                                            fontSize: '11px',
                                            fontWeight: '500',
                                          }}
                                        >
                                          {t(`shared.${issue?.status.toLowerCase().replace(/\s+/g, '_')}`, formatEnumName(issue?.status))}
                                        </Badge>
                                      </td>
                                        <td className="py-3">
                                        <div className="d-flex align-items-center">
                                          <span className="fw-medium">
                                            {issue.reason || t('shared.n_a', 'N/A')}
                                          </span>
                                        </div>
                                      </td>
                                    </motion.tr>
                                  );
                                })}
                              </AnimatePresence>
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <div className="avatar-lg bg-light rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                            <i className="bx bx-search text-muted fs-1"></i>
                          </div>
                          <h5 className="text-muted">{t('shared.no_planning_requests_found', 'No planning requests found')}</h5>
                          <p className="text-muted mb-0">
                            {searchTerm || fromDate || toDate 
                              ? t('shared.try_adjusting_filters', 'Try adjusting your filters or search terms')
                              : t('business.components.asset_modal.planning.empty_message', 'This asset has no reported planning requests')
                            }
                          </p>
                        </div>
                      )}
                    </div>
                      </CardBody>
                    </Card>
                  </div>
                </TabPane>

                {/* Warranties Tab - Updated UI */}
                <TabPane tabId="warranties">
                  <div className="p-4">
                    <Card className="border-0 shadow-sm">
                      <CardBody className="p-4">
                        {/* Header Section */}
                        <div className="d-flex align-items-center justify-content-between mb-4">
                          <div className="d-flex align-items-center">
                            <div className="info-icon me-3">
                              <div className="bg-success bg-opacity-50 rounded-circle d-flex align-items-center justify-content-center"
                                   style={{ width: '40px', height: '40px' }}>
                                <i className="bx bx-shield-check text-success fs-5"></i>
                              </div>
                            </div>
                            <div>
                              <h5 className="mb-1 fw-bold">{t('shared.service_contracts', 'Service Contracts')}</h5>
                              <p className="text-muted small mb-0">{t('business.components.asset_modal.warranties.subtitle', 'Manage Service contracts')}</p>
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            <Button
                              color="success"
                              size="sm"
                              onClick={() => handleWarrantyModal()}
                              className="d-flex align-items-center gap-2"
                            >
                              <i className="bx bx-plus"></i>
                              {t('shared.add_service_contract', 'Add Service Contract')}
                            </Button>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={handleAssignContractModal}
                              className="d-flex align-items-center gap-2"
                            >
                              <i className="bx bx-link"></i>
                              {t('shared.assign_contract', 'Assign Contract')}
                            </Button>
                          </div>
                        </div>

                        {/* Search Section */}
                        <Row className="mb-4">
                          <Col md={6}>
                            <InputGroup>
                              <span className="input-group-text bg-transparent border-end-0">
                                <i className="bx bx-search text-muted"></i>
                              </span>
                              <Input
                                type="text"
                                placeholder={t('shared.search_contracts_by_vendor_contract_numb', 'Search contracts by vendor, contract number, or notes...')}
                                value={warrantySearchTerm}
                                onChange={(e) => setWarrantySearchTerm(e.target.value)}
                                className="border-start-0 ps-0"
                              />
                            </InputGroup>
                          </Col>
                        </Row>

                        {/* Warranties Content */}
                        <div style={{ maxHeight: 'calc(90vh - 400px)', overflow: 'auto' }}>
                          {loadingWarranties ? (
                            <div className="text-center py-5">
                              <Spinner color="primary" size="lg" />
                              <p className="text-muted mt-3">{t('shared.loading_contracts_message', 'Loading service contracts...')}</p>
                            </div>
                          ) : filteredWarranties.length > 0 ? (
                            <div className="warranty-grid">
                              <AnimatePresence>
                                {filteredWarranties.map((warranty, index) => (
                                  <motion.div
                                    key={warranty?.id || index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.3, delay: index * 0.1 }}
                                    className="mb-4"
                                  >
                                    <Card className="border-0 shadow-sm warranty-card h-100">
                                      <CardBody className="p-4">
                                        {/* Card Header */}
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                          <div className="d-flex align-items-center">
                                            <div className="avatar-xs bg-success bg-opacity-10 rounded-circle me-2 d-flex align-items-center justify-content-center">
                                              <i className="bx bx-shield-check text-success"></i>
                                            </div>
                                            <div>
                                              <h6 className="mb-1 fw-bold">{t('shared.service_contract', 'Service Contract')} #{index + 1}</h6>
                                              <Badge 
                                                color={getWarrantyStatusBadge(warranty)}
                                                className="px-2 py-1"
                                                style={{ fontSize: '10px' }}
                                              >
                                                {warranty?.overallStatus ? formatEnumName(warranty.overallStatus) : t('shared.unknown', 'Unknown')}
                                              </Badge>
                                            </div>
                                          </div>
                                          <div className="d-flex gap-2">
                                            <Button
                                              color="outline-warning"
                                              size="sm"
                                              className="btn-sm-icon"
                                              onClick={() => handleWarrantyModal(warranty)}
                                              title={t('shared.button_edit_warranty', 'Edit warranty')}
                                            >
                                              <FeatherIcon icon="edit" size={14} />
                                            </Button>
                                            <Button
                                              color="outline-danger"
                                              size="sm"
                                              className="btn-sm-icon"
                                              onClick={() => handleDeleteModal(warranty)}
                                              title={t('shared.button_delete_warranty', 'Delete warranty')}
                                            >
                                              <FeatherIcon icon="trash-2" size={14} />
                                            </Button>
                                          </div>
                                        </div>

                                        {/* Warranty Information Grid */}
                                        <Row className="g-3">
                                          {/* Manufacturer Warranty */}
                                          <Col md={6}>
                                            <div className="info-section bg-light rounded p-3">
                                              <h6 className="text-primary mb-2 fw-semibold">
                                                <i className="bx bx-shield me-1"></i>{t('shared.manufacturer_warranty', 'Manufacturer Warranty')}
                                              </h6>
                                              <div className="info-item mb-2">
                                                <small className="text-muted">{t('shared.status', 'Status')}:</small>
                                                <div className="fw-medium">
                                                  {warranty?.hasManufacturerWarranty ? (
                                                    <Badge
                                                      color={
                                                        warranty?.manufacturerWarrantyStatus === 'Active' ? 'success' :
                                                        warranty?.manufacturerWarrantyStatus === 'Expired' ? 'danger' : 'secondary'
                                                      }
                                                      className="ms-1"
                                                      >
                                                        {warranty?.manufacturerWarrantyStatus ? formatEnumName(warranty.manufacturerWarrantyStatus) : t('shared.active', 'Active')}
                                                      </Badge>
                                                    ) : (
                                                      <Badge color="secondary" className="ms-1">{t('shared.not_available', 'Not Available')}</Badge>
                                                    )}
                                                </div>
                                              </div>
                                              {warranty?.hasManufacturerWarranty && (
                                                <>
                                                  <div className="info-item mb-2">
                                                    <small className="text-muted">{t('shared.period', 'Period')}:</small>
                                                    <div className="fw-medium small">
                                                        {warranty?.manufacturerWarrantyStartDate
                                                          ? new Date(warranty.manufacturerWarrantyStartDate).toLocaleDateString()
                                                          : t('shared.n_a', 'N/A')
                                                        } - {warranty?.manufacturerWarrantyEndDate
                                                          ? new Date(warranty.manufacturerWarrantyEndDate).toLocaleDateString()
                                                          : t('shared.n_a', 'N/A')
                                                        }
                                                    </div>
                                                  </div>
                                                  <div className="info-item">
                                                    <small className="text-muted">{t('shared.days_remaining', 'Days Remaining')}:</small>
                                                    <div className="fw-medium">
                                                      {warranty?.manufacturerWarrantyDaysRemaining || t('shared.n_a', 'N/A')}
                                                    </div>
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          </Col>

                                          {/* Warranty Extension */}
                                          <Col md={6}>
                                            <div className="info-section bg-light rounded p-3">
                                              <h6 className="text-info mb-2 fw-semibold">
                                                <i className="bx bx-shield-plus me-1"></i>{t('shared.warranty_extension', 'Warranty Extension')}
                                              </h6>
                                              <div className="info-item mb-2">
                                                <small className="text-muted">{t('shared.status', 'Status')}:</small>
                                                <div className="fw-medium">
                                                  {warranty?.hasWarrantyExtension ? (
                                                    <Badge
                                                      color={
                                                        warranty?.warrantyExtensionStatus === 'Active' ? 'success' :
                                                        warranty?.warrantyExtensionStatus === 'Expired' ? 'danger' : 'secondary'
                                                      }
                                                      className="ms-1"
                                                      >
                                                        {warranty?.warrantyExtensionStatus ? formatEnumName(warranty.warrantyExtensionStatus) : t('shared.active', 'Active')}
                                                      </Badge>
                                                    ) : (
                                                      <Badge color="secondary" className="ms-1">{t('shared.not_available', 'Not Available')}</Badge>
                                                    )}
                                                </div>
                                              </div>
                                              {warranty?.hasWarrantyExtension && (
                                                <>
                                                  <div className="info-item mb-2">
                                                    <small className="text-muted">{t('shared.period', 'Period')}:</small>
                                                    <div className="fw-medium small">
                                                        {warranty?.warrantyExtensionStartDate
                                                          ? new Date(warranty.warrantyExtensionStartDate).toLocaleDateString()
                                                          : t('shared.n_a', 'N/A')
                                                        } - {warranty?.warrantyExtensionEndDate
                                                          ? new Date(warranty.warrantyExtensionEndDate).toLocaleDateString()
                                                          : t('shared.n_a', 'N/A')
                                                        }
                                                    </div>
                                                  </div>
                                                  <div className="info-item">
                                                    <small className="text-muted">{t('shared.days_remaining', 'Days Remaining')}:</small>
                                                    <div className="fw-medium">
                                                      {warranty?.warrantyExtensionDaysRemaining || t('shared.n_a', 'N/A')}
                                                    </div>
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          </Col>

                                          {/* Maintenance Contract */}
                                          <Col md={12}>
                                            <div className="info-section bg-light rounded p-3">
                                              <h6 className="text-warning mb-2 fw-semibold">
                                                <i className="bx bx-wrench me-1"></i>{t('shared.maintenance_contract', 'Maintenance Contract')}
                                              </h6>
                                              <Row className="g-2">
                                                <Col md={3}>
                                                  <div className="info-item">
                                                    <small className="text-muted">{t('shared.contract_type', 'Contract Type')}:</small>
                                                    <div className="fw-medium">
                                                      {(() => {
                                                        const type = warranty?.maintenanceContractType || '';
                                                        if (type === 'NO_CONTRACT') return t('shared.maintenance_contract_types_no_contract', 'No Contract');
                                                        const typeKey = type.split(' ').map(word => word[0]?.toLowerCase()).filter(char => char && char.match(/[a-z]/i)).join('');
                                                        return t(`shared.maintenance_contract_types_${typeKey}`, type) || t('shared.n_a', 'N/A');
                                                      })()}
                                                    </div>
                                                  </div>
                                                </Col>
                                                {warranty?.maintenanceContractType && warranty?.maintenanceContractType !== 'NO_CONTRACT' && (
                                                  <>
                                                    <Col md={3}>
                                                      <div className="info-item">
                                                          <small className="text-muted">{t('shared.vendor', 'Vendor')}:</small>
                                                          <div className="fw-medium">{warranty?.maintenanceVendorName || t('shared.n_a', 'N/A')}</div>
                                                      </div>
                                                    </Col>
                                                    <Col md={3}>
                                                      <div className="info-item">
                                                          <small className="text-muted">{t('shared.contract_number_short', 'Contract #')}:</small>
                                                          <div className="fw-medium">{warranty?.contractNumber || t('shared.n_a', 'N/A')}</div>
                                                      </div>
                                                    </Col>
                                                    <Col md={3}>
                                                      <div className="info-item">
                                                        <small className="text-muted">{t('shared.value', 'Value')}:</small>
                                                        <div className="fw-medium">{warranty?.contractValueFormatted || t('shared.n_a', 'N/A')}</div>
                                                      </div>
                                                    </Col>
                                                    <Col md={6}>
                                                      <div className="info-item">
                                                        <small className="text-muted">{t('business.components.asset_modal.warranties.contract_period', 'Contract Period')}:</small>
                                                        <div className="fw-medium small">
                                                          {warranty?.contractStartDate
                                                            ? new Date(warranty.contractStartDate).toLocaleDateString()
                                                            : t('shared.n_a', 'N/A')
                                                          } - {warranty?.contractEndDate
                                                            ? new Date(warranty.contractEndDate).toLocaleDateString()
                                                            : t('shared.n_a', 'N/A')
                                                          }
                                                        </div>
                                                      </div>
                                                    </Col>
                                                    <Col md={6}>
                                                      <div className="info-item">
                                                        <small className="text-muted">{t('business.components.asset_modal.warranties.contract_status', 'Contract Status')}:</small>
                                                        <div className="fw-medium">
                                                          <Badge
                                                            color={
                                                              warranty?.contractStatus === 'Active' ? 'success' :
                                                              warranty?.contractStatus === 'Expired' ? 'danger' : 'secondary'
                                                            }
                                                            className="ms-1"
                                                          >
                                                            {warranty?.contractStatus ? formatEnumName(warranty.contractStatus) : t('shared.n_a', 'N/A')}
                                                          </Badge>
                                                        </div>
                                                      </div>
                                                    </Col>
                                                  </>
                                                )}
                                              </Row>
                                            </div>
                                          </Col>

                                          {/* Notes */}
                                          {warranty?.notes && (
                                            <Col md={12}>
                                              <div className="info-section bg-light rounded p-3">
                                                <h6 className="text-secondary mb-2 fw-semibold">
                                                  <i className="bx bx-note me-1"></i>{t('shared.notes', 'Notes')}
                                                </h6>
                                                <div className="text-wrap" style={{ lineHeight: '1.5' }}>
                                                  {warranty.notes}
                                                </div>
                                              </div>
                                            </Col>
                                          )}

                                          {/* Attachments */}
                                          {warranty?.attachmentUrls && warranty.attachmentUrls.length > 0 && (
                                            <Col md={12}>
                                              <div className="info-section bg-light rounded p-3">
                                                <h6 className="text-warning mb-3 fw-semibold">
                                                  <i className="bx bx-paperclip me-1"></i>{t('shared.attachments', 'Attachments')}
                                                </h6>
                                                <div className="d-flex flex-wrap gap-2">
                                                  {warranty.attachmentUrls.map((url, urlIndex) => {
                                                    const isImage = url.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?|$)/i);
                                                    const isPdf = url.match(/\.pdf(\?|$)/i);
                                                    const isDoc = url.match(/\.(doc|docx)(\?|$)/i);
                                                    const isExcel = url.match(/\.(xls|xlsx)(\?|$)/i);
                                                    const fileName = url.split('/').pop().split('?')[0];

                                                    return (
                                                      <div key={urlIndex} className="position-relative">
                                                        <a
                                                          href={url}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="d-block"
                                                        >
                                                          {isImage ? (
                                                            <img
                                                              src={url}
                                                              alt={`Attachment ${urlIndex + 1}`}
                                                              className="rounded border"
                                                              style={{
                                                                width: '100px',
                                                                height: '100px',
                                                                objectFit: 'cover',
                                                                cursor: 'pointer',
                                                                transition: 'transform 0.2s'
                                                              }}
                                                              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                                                              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                                            />
                                                          ) : (
                                                            <div
                                                              className="rounded border d-flex flex-column align-items-center justify-content-center p-2"
                                                              style={{
                                                                width: '100px',
                                                                height: '100px',
                                                                cursor: 'pointer',
                                                                backgroundColor: '#fff',
                                                                transition: 'transform 0.2s'
                                                              }}
                                                              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                                            >
                                                              <FeatherIcon
                                                                icon={
                                                                  isPdf ? 'file-text' :
                                                                  isDoc ? 'file-text' :
                                                                  isExcel ? 'grid' :
                                                                  'file'
                                                                }
                                                                size={32}
                                                                className="mb-1"
                                                                style={{ color: '#6c757d' }}
                                                              />
                                                              <small className="text-muted text-center" style={{ wordBreak: 'break-word', fontSize: '9px' }}>
                                                                {fileName.length > 15 ? fileName.substring(0, 12) + '...' : fileName}
                                                              </small>
                                                            </div>
                                                          )}
                                                        </a>
                                                        <Badge
                                                          color="dark"
                                                          className="position-absolute top-0 end-0 m-1"
                                                          style={{ fontSize: '9px' }}
                                                        >
                                                          {urlIndex + 1}
                                                        </Badge>
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              </div>
                                            </Col>
                                          )}
                                        </Row>
                                      </CardBody>
                                    </Card>
                                  </motion.div>
                                ))}
                              </AnimatePresence>
                            </div>
                          ) : (
                            <div className="text-center py-5">
                              <div className="avatar-lg bg-light rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                                <i className="bx bx-shield-check text-muted fs-1"></i>
                              </div>
                              <h5 className="text-muted">{t('business.components.asset_modal.warranties.empty_title', 'No service contract found')}</h5>
                              <p className="text-muted mb-0">
                                {warrantySearchTerm
                                  ? t('business.components.asset_modal.warranties.search_empty_message', 'No service contract match your search criteria')
                                  : t('business.components.asset_modal.warranties.empty_message', 'No service contract have been added for this asset')}
                              </p>
                              <Button
                                color="success"
                                className="mt-3"
                                onClick={() => handleWarrantyModal()}
                              >
                                <i className="bx bx-plus me-2"></i>
                                {t('business.components.asset_modal.warranties.add_first_button', 'Add First Service Contract')}
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                </TabPane>

                <TabPane tabId="tco">
                  <div className="p-4">
                    {!isRequestor && assetData?.id && (
                      <>
                        <div className="mb-4">
                          <AssetTCOPanel assetId={assetData.id} />
                        </div>
                        <div>
                          <AssetCostRollupCard assetId={assetData.id} />
                        </div>
                      </>
                    )}
                  </div>
                </TabPane>

                {/* Sub Assets Tab */}
                <TabPane tabId="subAssets">
                  <div className="p-4">
                    <Card className="border-0 shadow-sm">
                      <CardBody className="p-4">
                        {/* Header Section */}
                        <div className="d-flex align-items-center justify-content-between mb-4">
                          <div className="d-flex align-items-center">
                            <div className="info-icon me-3">
                              <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                   style={{ width: '40px', height: '40px' }}>
                                <i className="bx bx-sitemap text-info fs-5"></i>
                              </div>
                            </div>
                            <div>
                              <h5 className="mb-1 fw-bold">
                                {t('shared.sub_assets', 'Sub Assets')}
                                {subAssets.length > 0 && (
                                  <span className="text-muted ms-2">({subAssets.length})</span>
                                )}
                              </h5>
                              <p className="text-muted small mb-0">{t('business.components.asset_modal.sub_assets.subtitle', 'Manage sub assets under this parent asset')}</p>
                            </div>
                          </div>
                          <Button
                            color="info"
                            size="sm"
                            onClick={handleCreateSubAssetClick}
                            className="d-flex align-items-center gap-2"
                          >
                            <FeatherIcon icon="plus" size={14} />
                            {t('shared.add_sub_asset', 'Add Sub Asset')}
                          </Button>
                        </div>

                        {/* Sub Assets Tree */}
                        <div style={{ maxHeight: 'calc(90vh - 400px)', overflow: 'auto' }}>
                          {loadingSubAssets ? (
                            <div className="text-center py-5">
                              <Spinner color="primary" size="lg" />
                              <p className="text-muted mt-3">{t('shared.loading_sub_assets', 'Loading sub assets...')}</p>
                            </div>
                          ) : subAssets.length > 0 ? (
                            <div>
                              {subAssets.map((subAsset) => renderTreeNode(subAsset, 0))}
                            </div>
                          ) : (
                            <div className="text-center py-5">
                              <div className="avatar-lg bg-light rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                                <i className="bx bx-sitemap text-muted fs-1"></i>
                              </div>
                              <h5 className="text-muted">{t('shared.no_sub_assets_found', 'No sub assets found')}</h5>
                              <p className="text-muted mb-0">
                                {t('shared.no_sub_assets_have_been_added_for_this_a', 'No sub assets have been added for this asset')}
                              </p>
                              <Button
                                color="info"
                                className="mt-3"
                                onClick={handleCreateSubAssetClick}
                              >
                                <FeatherIcon icon="plus" size={14} className="me-2" />
                                {t('business.components.asset_modal.sub_assets.add_first_button', 'Add First Sub Asset')}
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                </TabPane>


                {/* Activities Tab */}
                <TabPane tabId="activities">
                  <div className="p-4">
                    <Card className="border-0 shadow-sm">
                      <CardBody className="p-4">
                        <div className="d-flex align-items-center mb-4">
                          <div className="info-icon me-3">
                            <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                 style={{ width: '40px', height: '40px' }}>
                              <FeatherIcon icon="activity" size={20} className="text-info" />
                            </div>
                          </div>
                          <div>
                            <h5 className="mb-1 fw-bold">{t('shared.activity_timeline', 'Activity Timeline')}</h5>
                            <p className="text-muted small mb-0">{t('shared.complete_history_of_asset_activities', 'Complete history of asset activities')}</p>
                          </div>
                        </div>

                        {loadingActivities ? (
                          <div className="text-center py-5">
                            <Spinner color="primary" size="lg" />
                            <p className="text-muted mt-3">{t('shared.loading_activity_log', 'Loading activity log...')}</p>
                          </div>
                        ) : activities.length > 0 ? (
                          <div className="activity-timeline" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {activities.map((activity, index) => (
                              <motion.div 
                                key={activity?.id || index} 
                                className="timeline-item d-flex mb-4"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1 }}
                              >
                                <div className="timeline-marker me-3">
                                  <div className="timeline-dot bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                       style={{ width: '32px', height: '32px', minWidth: '32px' }}>
                                    <FeatherIcon icon="clock" size={14} className="text-white" />
                                  </div>
                                  {index < activities.length - 1 && (
                                    <div className="timeline-line bg-light" 
                                         style={{ width: '2px', height: '40px', marginLeft: '15px', marginTop: '4px' }}></div>
                                  )}
                                </div>
                                <div className="timeline-content flex-grow-1">
                                  <div className="timeline-card bg-light rounded p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <div>
                                        <div className="fw-semibold text-dark">{activity.userName}</div>
                                        <div className="text-muted">{translateActivity(activity, t)}</div>
                                      </div>
                                      <Badge color="light" className="text-muted small">
                                        <FeatherIcon icon="calendar" size={12} className="me-1" />
                                        {formatCreatedDate(activity?.createdAt)}
                                        {activity?.createdAt && (
                                          <span className="ms-1">
                                            {new Date(activity.createdAt).toLocaleTimeString(navigator.language || 'en-IN', { hour: '2-digit', minute: '2-digit' })}
                                          </span>
                                        )}
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-5">
                            <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                 style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                              <FeatherIcon icon="clock" size={24} className="text-muted" />
                            </div>
                             <h5 className="text-muted">{t('shared.no_activity_recorded', 'No activity recorded')}</h5>
                             <p className="text-muted mb-0">{t('shared.activity_will_appear_here_as_the_asset_p', 'Activity will appear here as the asset progresses')}</p>
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </div>
                </TabPane>

                                    {/* Spare Parts Tab */}
                                    <TabPane tabId="spareParts">
                                      <div className="p-4">
                                        <Card className="border-0 shadow-sm">
                                          <CardBody className="p-4">
                                            <div className="d-flex align-items-center mb-4">
                                              <div className="info-icon me-3">
                                                <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                                     style={{ width: '40px', height: '40px' }}>
                                                  <FeatherIcon icon="tool" size={20} className="text-primary" />
                                                </div>
                                              </div>
                                              <div className="flex-grow-1">
                                                <h5 className="mb-1 fw-bold">{t("shared.spare_parts_usage", "Spare Parts Usage")}</h5>
                                                <p className="text-muted small mb-0">{t("shared.no_spare_parts_have_been_used_for_this_a", "No spare parts have been used for this asset")}</p>
                                              </div>
                                              <Button
                                                color="primary"
                                                size="sm"
                                                onClick={handleOpenAttachSparePart}
                                              >
                                                <FeatherIcon icon="plus" size={14} className="me-1" />
                                                {t("business.asset_detail.attach_spare_part", "Attach Spare Part")}
                                              </Button>
                                            </div>
                
                                            {loadingSparePartsConsumables ? (
                                              <div className="text-center py-5">
                                                <Spinner color="primary" size="lg" />
                                                <p className="text-muted mt-3">{t("shared.loading_spare_parts", "Loading spare parts...")}</p>
                                              </div>
                                            ) : sparePartsUsage.length > 0 ? (
                                              <div className="table-responsive">
                                                <Table hover className="mb-0">
                                                  <thead className="bg-light">
                                                     <tr>
                                                       <th>#</th>
                                                       <th>{t('shared.spare_part_name', 'Spare Part Name')}</th>
                                                       <th>{t('shared.reference_number', 'Reference Number')}</th>
                                                       <th>{t('shared.quantity', 'Quantity')}</th>
                                                       <th>{t('shared.status', 'Status')}</th>
                                                       <th>{t('shared.purchase_date', 'Purchase Date')}</th>
                                                       <th>{t('shared.receipt_date', 'Receipt Date')}</th>
                                                       <th>{t('shared.batch_number', 'Batch Number')}</th>
                                                       <th>{t('shared.expiry_date', 'Expiry Date')}</th>
                                                       <th>{t('shared.actions', 'Actions')}</th>
                                                     </tr>
                                                  </thead>
                                                  <tbody>
                                                    <AnimatePresence>
                                                      {sparePartsUsage.map((sparePart, index) => (
                                                        <motion.tr
                                                          key={sparePart.id || `sparePart-${index}`}
                                                          initial={{ opacity: 0, y: 10 }}
                                                          animate={{ opacity: 1, y: 0 }}
                                                          exit={{ opacity: 0, y: -10 }}
                                                          transition={{ duration: 0.2, delay: index * 0.05 }}
                                                        >
                                                          <td>{index + 1}</td>
                                                           <td className="fw-medium">
                                                             {sparePart.sparePartsResponse?.name || t('shared.n_a', 'N/A')}
                                                           </td>
                                                           <td>
                                                             <code className="text-primary">{sparePart.referenceNumber || t('shared.n_a', 'N/A')}</code>
                                                           </td>
                                                          <td>
                                                            <Badge color="info" pill>{sparePart.quantity}</Badge>
                                                          </td>
                                                          <td>
                                                            <Badge
                                                              color={sparePart.status === 'APPROVED' ? 'success' : sparePart.status === 'PENDING' ? 'warning' : 'secondary'}
                                                              pill
                                                            >
                                                              {sparePart.status ? t(`shared.status_${sparePart.status.toLowerCase()}`, sparePart.status) : t('shared.n_a', 'N/A')}
                                                            </Badge>
                                                          </td>
                                                          <td>{sparePart.purchaseDate || t('shared.n_a', 'N/A')}</td>
                                                          <td>{sparePart.receiptDate || t('shared.n_a', 'N/A')}</td>
                                                          <td>{sparePart.batchNumber || '-'}</td>
                                                          <td>{sparePart.expiryDate || t('shared.n_a', 'N/A')}</td>
                                                        </motion.tr>
                                                      ))}
                                                    </AnimatePresence>
                                                  </tbody>
                                                </Table>
                                              </div>
                                            ) : (
                                              <div className="text-center py-5">
                                                <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                                     style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                                                  <FeatherIcon icon="tool" size={24} className="text-muted" />
                                                </div>
                                                 <h5 className="text-muted">{t('shared.no_spare_parts_usage', 'No spare parts usage')}</h5>
                                                 <p className="text-muted mb-0">{t('shared.no_spare_parts_have_been_used_for_this_a', 'No spare parts have been used for this asset')}</p>
                                              </div>
                                            )}
                                          </CardBody>
                                        </Card>
                                      </div>
                                    </TabPane>

                                    {/* Meters Tab */}
                                    <TabPane tabId="meters">
                                      <div className="p-4">
                                        <Card className="border-0 shadow-sm">
                                          <CardBody className="p-4">
                                            <div className="d-flex align-items-center mb-4">
                                              <div className="info-icon me-3">
                                                <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                                     style={{ width: '40px', height: '40px' }}>
                                                  <FeatherIcon icon="activity" size={20} className="text-primary" />
                                                </div>
                                              </div>
                                              <div>
                                                <h5 className="mb-1 fw-bold">{t('shared.meters', 'Meters')}</h5>
                                                <p className="text-muted small mb-0">{t('meters.asset_meters_desc', 'Meters tracking usage and conditions for this asset')}</p>
                                              </div>
                                            </div>

                                            {loadingMeters ? (
                                              <div className="text-center py-5">
                                                <Spinner color="primary" size="lg" />
                                                <p className="text-muted mt-3">{t('shared.loading_meters', 'Loading meters...')}</p>
                                              </div>
                                            ) : assetMeters.length > 0 ? (
                                              <div className="table-responsive">
                                                <Table hover className="mb-0">
                                                  <thead className="bg-light">
                                                    <tr>
                                                      <th>{t('shared.name', 'Name')}</th>
                                                      <th>{t('shared.unit', 'Unit')}</th>
                                                      <th>{t('meters.last_reading', 'Last Reading')}</th>
                                                      <th>{t('shared.status', 'Status')}</th>
                                                      <th>{t('meters.last_read_date', 'Last Read Date')}</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    <AnimatePresence>
                                                      {assetMeters.map((meter, index) => (
                                                        <motion.tr
                                                          key={meter.id || `meter-${index}`}
                                                          initial={{ opacity: 0, y: 10 }}
                                                          animate={{ opacity: 1, y: 0 }}
                                                          exit={{ opacity: 0, y: -10 }}
                                                          transition={{ duration: 0.2, delay: index * 0.05 }}
                                                        >
                                                          <td className="fw-medium">
                                                            <span
                                                              className="text-primary cursor-pointer hover-underline"
                                                              style={{ cursor: 'pointer' }}
                                                              onClick={() => setDetailMeterId(meter.id)}
                                                            >
                                                              {meter.name}
                                                            </span>
                                                          </td>
                                                          <td>{meter.measurementUnit || '-'}</td>
                                                          <td>{meter.lastReadingValue != null ? meter.lastReadingValue : '-'}</td>
                                                          <td>
                                                            <Badge
                                                              color={METER_STATUS_COLORS[meter.status] || 'secondary'}
                                                              pill
                                                            >
                                                              {meter.status ? t(`meters.status_${meter.status.toLowerCase()}`, meter.status) : '-'}
                                                            </Badge>
                                                          </td>
                                                          <td>
                                                            {meter.lastReadingAt
                                                              ? new Date(meter.lastReadingAt).toLocaleDateString('en-GB')
                                                              : '-'}
                                                          </td>
                                                        </motion.tr>
                                                      ))}
                                                    </AnimatePresence>
                                                  </tbody>
                                                </Table>
                                              </div>
                                            ) : (
                                              <div className="text-center py-5">
                                                <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                                     style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                                                  <FeatherIcon icon="activity" size={24} className="text-muted" />
                                                </div>
                                                <h5 className="text-muted">{t('meters.no_meters', 'No meters found.')}</h5>
                                                <p className="text-muted mb-0">{t('meters.no_meters_for_asset', 'No meters have been configured for this asset')}</p>
                                              </div>
                                            )}
                                          </CardBody>
                                        </Card>
                                      </div>
                                    </TabPane>

                                    {/* Service Requests Tab (Requestor only) */}
                                    {isRequestor && (
                                      <TabPane tabId="serviceRequests">
                                        <div className="p-4">
                                          {selectedServiceRequest ? (
                                            <Card className="border-0 shadow-sm">
                                              <CardBody className="p-4">
                                                <div className="d-flex align-items-center mb-4">
                                                  <Button
                                                    color="link"
                                                    className="p-0 me-3 text-muted"
                                                    onClick={() => setSelectedServiceRequest(null)}
                                                  >
                                                    <FeatherIcon icon="arrow-left" size={20} />
                                                  </Button>
                                                   <div>
                                                     <h5 className="mb-1 fw-bold">{selectedServiceRequest.requestNumber || t('shared.request_details', 'Request Details')}</h5>
                                                     <p className="text-muted small mb-0">{selectedServiceRequest.title}</p>
                                                   </div>
                                                </div>
                                                <Row className="g-4">
                                                  <Col md={6}>
                                                    <div className="bg-light rounded p-3 h-100">
                                                       <h6 className="text-primary mb-3 fw-semibold">
                                                         <FeatherIcon icon="info" size={16} className="me-2" />
                                                         {t('shared.basic_information', 'Basic Information')}
                                                       </h6>
                                                       <div className="mb-3">
                                                         <label className="text-muted small">{t('shared.title', 'Title')}</label>
                                                        <div className="fw-medium">{selectedServiceRequest.title}</div>
                                                      </div>
                                                       <div className="mb-3">
                                                         <label className="text-muted small">{t('shared.status', 'Status')}</label>
                                                        <div>
                                                          <Badge color={
                                                            selectedServiceRequest.status === 'PENDING' ? 'info' :
                                                            selectedServiceRequest.status === 'APPROVED' ? 'success' :
                                                            selectedServiceRequest.status === 'DECLINED' ? 'danger' :
                                                            selectedServiceRequest.status === 'DONE' ? 'primary' : 'secondary'
                                                           }>
                                                             {selectedServiceRequest.status ? t(`shared.status_${selectedServiceRequest.status.toLowerCase()}`, formatEnumName(selectedServiceRequest.status)) : t('shared.n_a', 'N/A')}
                                                           </Badge>
                                                        </div>
                                                      </div>
                                                       <div className="mb-3">
                                                         <label className="text-muted small">{t('shared.urgency_level', 'Urgency Level')}</label>
                                                        <div>
                                                          {selectedServiceRequest.urgencyLevel ? (
                                                            <Badge className={
                                                              selectedServiceRequest.urgencyLevel === 'HIGH' ? 'bg-danger' :
                                                              selectedServiceRequest.urgencyLevel === 'MEDIUM' ? 'bg-info' :
                                                              selectedServiceRequest.urgencyLevel === 'LOW' ? 'bg-success' :
                                                              selectedServiceRequest.urgencyLevel === 'SEVERE' ? 'bg-dark' : 'bg-secondary'
                                                            }>
                                                               {selectedServiceRequest.urgencyLevel ? t(`shared.urgency_${selectedServiceRequest.urgencyLevel.toLowerCase()}`, formatEnumName(selectedServiceRequest.urgencyLevel)) : t('shared.n_a', 'N/A')}
                                                             </Badge>
                                                           ) : '-'}
                                                         </div>
                                                       </div>
                                                       <div className="mb-0">
                                                         <label className="text-muted small">{t('shared.usage_status', 'Usage Status')}</label>
                                                         <div className="fw-medium">
                                                           {selectedServiceRequest.usageStatus ? t(`shared.status_${selectedServiceRequest.usageStatus.toLowerCase()}`, formatEnumName(selectedServiceRequest.usageStatus)) : '-'}
                                                         </div>
                                                       </div>
                                                     </div>
                                                   </Col>
                                                   <Col md={6}>
                                                     <div className="bg-light rounded p-3 h-100">
                                                       <h6 className="text-primary mb-3 fw-semibold">
                                                         <FeatherIcon icon="map-pin" size={16} className="me-2" />
                                                         {t('shared.asset_location', 'Asset & Location')}
                                                       </h6>
                                                       <div className="mb-3">
                                                         <label className="text-muted small">{t('shared.asset', 'Asset')}</label>
                                                         <div className="fw-medium">{selectedServiceRequest.asset?.assetName || '-'}</div>
                                                       </div>
                                                       <div className="mb-3">
                                                         <label className="text-muted small">{t('shared.location', 'Location')}</label>
                                                         <div className="fw-medium">{selectedServiceRequest.locationName || '-'}</div>
                                                       </div>
                                                       <div className="mb-3">
                                                         <label className="text-muted small">{t('shared.requested_by', 'Requested By')}</label>
                                                         <div className="fw-medium">{selectedServiceRequest.requestedBy?.name || '-'}</div>
                                                       </div>
                                                       <div className="mb-0">
                                                         <label className="text-muted small">{t('shared.created_at', 'Created At')}</label>
                                                         <div className="fw-medium">{formatCreatedDate(selectedServiceRequest.createdAt)}</div>
                                                       </div>
                                                     </div>
                                                   </Col>
                                                   {selectedServiceRequest.description && (
                                                     <Col md={12}>
                                                       <div className="bg-light rounded p-3">
                                                         <h6 className="text-primary mb-3 fw-semibold">
                                                           <FeatherIcon icon="file-text" size={16} className="me-2" />
                                                           {t('shared.description', 'Description')}
                                                         </h6>
                                                         <p className="mb-0">{selectedServiceRequest.description}</p>
                                                       </div>
                                                     </Col>
                                                   )}
                                                   {selectedServiceRequest.imageUrls?.length > 0 && (
                                                     <Col md={12}>
                                                       <div className="bg-light rounded p-3">
                                                         <h6 className="text-primary mb-3 fw-semibold">
                                                           <FeatherIcon icon="image" size={16} className="me-2" />
                                                           {t('shared.images', 'Images')}
                                                         </h6>
                                                         <div className="d-flex gap-2 flex-wrap">
                                                           {selectedServiceRequest.imageUrls.map((url, idx) => (
                                                             <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                                                               <img
                                                                 src={url}
                                                                 alt={`Attachment ${idx + 1}`}
                                                                 style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }}
                                                               />
                                                             </a>
                                                           ))}
                                                         </div>
                                                       </div>
                                                     </Col>
                                                   )}
                                                   {selectedServiceRequest.documentUrls?.length > 0 && (
                                                     <Col md={12}>
                                                       <div className="bg-light rounded p-3">
                                                         <h6 className="text-primary mb-3 fw-semibold">
                                                           <FeatherIcon icon="paperclip" size={16} className="me-2" />
                                                           {t('shared.documents', 'Documents')}
                                                         </h6>
                                                         <div className="d-flex gap-2 flex-wrap">
                                                           {selectedServiceRequest.documentUrls.map((url, idx) => (
                                                             <a
                                                               key={idx}
                                                               href={url}
                                                               target="_blank"
                                                               rel="noopener noreferrer"
                                                               className="btn btn-sm btn-outline-primary"
                                                             >
                                                               <FeatherIcon icon="file" size={14} className="me-1" />
                                                               {t('shared.document', 'Document')} {idx + 1}
                                                             </a>
                                                           ))}
                                                         </div>
                                                       </div>
                                                     </Col>
                                                   )}
                                                 </Row>
                                              </CardBody>
                                            </Card>
                                          ) : (
                                            <Card className="border-0 shadow-sm">
                                              <CardBody className="p-4">
                                                <div className="d-flex align-items-center mb-4">
                                                  <div className="info-icon me-3">
                                                    <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                                         style={{ width: '40px', height: '40px' }}>
                                                      <FeatherIcon icon="clipboard" size={20} className="text-primary" />
                                                    </div>
                                                  </div>
                                                   <div>
                                                     <h5 className="mb-1 fw-bold">{t('shared.my_service_requests', 'My Service Requests')}</h5>
                                                     <p className="text-muted small mb-0">{t('business.components.asset_modal.service_requests.list_subtitle', 'Service requests you created for this asset')}</p>
                                                   </div>
                                                </div>

                                                 {loadingServiceRequests ? (
                                                   <div className="text-center py-5">
                                                     <Spinner color="primary" size="lg" />
                                                     <p className="text-muted mt-3">{t('business.components.asset_modal.service_requests.loading_message', 'Loading service requests...')}</p>
                                                   </div>
                                                ) : serviceRequests.length > 0 ? (
                                                  <div className="table-responsive">
                                                     <table className="table table-hover table-sm">
                                                       <thead className="table-light">
                                                         <tr>
                                                           <th>{t('shared.request_number_short', 'Request #')}</th>
                                                           <th>{t('shared.title', 'Title')}</th>
                                                           <th>{t('shared.status', 'Status')}</th>
                                                           <th>{t('shared.urgency', 'Urgency')}</th>
                                                           <th>{t('shared.location', 'Location')}</th>
                                                           <th>{t('shared.created', 'Created')}</th>
                                                         </tr>
                                                       </thead>
                                                      <tbody>
                                                        {serviceRequests.map((request, index) => (
                                                          <motion.tr
                                                            key={request.id || `sr-${index}`}
                                                            initial={{ opacity: 0, y: 10 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            transition={{ delay: index * 0.05 }}
                                                          >
                                                            <td>
                                                              <span
                                                                className="fw-semibold text-primary"
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => setSelectedServiceRequest(request)}
                                                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                              >
                                                                {request.requestNumber || `#${index + 1}`}
                                                              </span>
                                                            </td>
                                                            <td className="fw-medium">{request.title}</td>
                                                             <td>
                                                               <Badge color={
                                                                 request.status === 'PENDING' ? 'info' :
                                                                 request.status === 'APPROVED' ? 'success' :
                                                                 request.status === 'DECLINED' ? 'danger' :
                                                                 request.status === 'DONE' ? 'primary' : 'secondary'
                                                               }>
                                                                 {request.status ? t(`shared.status_${request.status.toLowerCase()}`, formatEnumName(request.status)) : t('shared.n_a', 'N/A')}
                                                               </Badge>
                                                             </td>
                                                             <td>
                                                               {request.urgencyLevel ? (
                                                                 <Badge className={
                                                                   request.urgencyLevel === 'HIGH' ? 'bg-danger' :
                                                                   request.urgencyLevel === 'MEDIUM' ? 'bg-info' :
                                                                   request.urgencyLevel === 'LOW' ? 'bg-success' :
                                                                   request.urgencyLevel === 'SEVERE' ? 'bg-dark' : 'bg-secondary'
                                                                 }>
                                                                   {request.urgencyLevel ? t(`shared.urgency_${request.urgencyLevel.toLowerCase()}`, formatEnumName(request.urgencyLevel)) : t('shared.n_a', 'N/A')}
                                                                 </Badge>
                                                               ) : '-'}
                                                             </td>
                                                             <td className="text-muted">{request.locationName || '-'}</td>
                                                             <td className="text-muted">{formatCreatedDate(request.createdAt)}</td>
                                                          </motion.tr>
                                                        ))}
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                ) : (
                                                  <div className="text-center py-5">
                                                    <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                                         style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                                                      <FeatherIcon icon="clipboard" size={24} className="text-muted" />
                                                    </div>
                                                     <h5 className="text-muted">{t('shared.no_service_requests', 'No service requests')}</h5>
                                                     <p className="text-muted mb-0">{t('business.components.asset_modal.service_requests.empty_message', "You haven't created any service requests for this asset")}</p>
                                                  </div>
                                                )}
                                              </CardBody>
                                            </Card>
                                          )}
                                        </div>
                                      </TabPane>
                                    )}

                                    {/* Documents Tab */}
                                    <TabPane tabId="documents">
                                      <div className="p-4">
                                        <Card className="border-0 shadow-sm">
                                          <CardBody className="p-4">
                                            {/* Header with upload button */}
                                            <div className="d-flex align-items-center justify-content-between mb-4">
                                              <div className="d-flex align-items-center">
                                                <div className="info-icon me-3">
                                                  <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                                       style={{ width: '40px', height: '40px' }}>
                                                    <FeatherIcon icon="file-text" size={20} className="text-info" />
                                                  </div>
                                                </div>
                                                 <div>
                                                   <h5 className="mb-1 fw-bold">{t('shared.documents', 'Documents')}</h5>
                                                   <p className="text-muted small mb-0">{t('business.components.asset_modal.documents.subtitle', 'PDF, Word, and Excel files')}</p>
                                                 </div>
                                              </div>
                                              <div>
                                                <Button
                                                  color="primary"
                                                  onClick={() => documentInputRef.current?.click()}
                                                  disabled={uploadingDocuments}
                                                  className="d-flex align-items-center gap-2"
                                                >
                                                   {uploadingDocuments ? (
                                                     <>
                                                       <Spinner size="sm" />
                                                       {t('shared.uploading', 'Uploading...')}
                                                     </>
                                                   ) : (
                                                     <>
                                                       <FeatherIcon icon="upload" size={16} />
                                                       {t('shared.upload_files', 'Upload Files')}
                                                     </>
                                                   )}
                                                </Button>
                                                <input
                                                  type="file"
                                                  ref={documentInputRef}
                                                  multiple
                                                  hidden
                                                  onChange={handleDocumentUpload}
                                                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                                                />
                                              </div>
                                            </div>

                                            {/* Validation Errors */}
                                            {documentErrors.length > 0 && (
                                              <div className="mb-4">
                                                {documentErrors.map((error, index) => (
                                                  <Alert
                                                    key={index}
                                                    color="danger"
                                                    className="py-2 mb-2 d-flex align-items-center justify-content-between"
                                                  >
                                                    <div className="d-flex align-items-center">
                                                      <FeatherIcon icon="alert-circle" size={16} className="me-2" />
                                                      <small>{error}</small>
                                                    </div>
                                                    <button
                                                      type="button"
                                                      className="btn-close"
                                                      style={{ fontSize: '0.7rem' }}
                                                      onClick={() => dismissDocumentError(index)}
                                                    />
                                                  </Alert>
                                                ))}
                                              </div>
                                            )}

                                            {/* Documents List */}
                                            {assetData?.documentUrls?.length > 0 ? (
                                              <div className="documents-list">
                                                {assetData.documentUrls.map((url, index) => (
                                                  <motion.div
                                                    key={index}
                                                    className="d-flex align-items-center p-3 bg-light rounded mb-2"
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.2, delay: index * 0.05 }}
                                                  >
                                                    <div
                                                      className="bg-white rounded d-flex align-items-center justify-content-center me-3"
                                                      style={{ width: '40px', height: '40px' }}
                                                    >
                                                      <FeatherIcon icon={getDocumentIcon(url)} size={20} className="text-primary" />
                                                    </div>
                                                    <div className="flex-grow-1 overflow-hidden">
                                                      <p className="mb-0 fw-medium text-truncate">{getFileNameFromUrl(url)}</p>
                                                       <small className="text-muted">
                                                         {url.split('.').pop()?.toUpperCase()} {t('shared.document', 'Document')}
                                                       </small>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                      <Button
                                                        color="outline-primary"
                                                        size="sm"
                                                        onClick={() => window.open(url, '_blank')}
                                                      >
                                                        <FeatherIcon icon="download" size={14} />
                                                      </Button>
                                                      <Button
                                                        color="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleDeleteDocument(url)}
                                                        disabled={deletingDocument === url}
                                                      >
                                                        {deletingDocument === url ? (
                                                          <Spinner size="sm" style={{ width: '14px', height: '14px' }} />
                                                        ) : (
                                                          <FeatherIcon icon="trash-2" size={14} />
                                                        )}
                                                      </Button>
                                                    </div>
                                                  </motion.div>
                                                ))}
                                              </div>
                                            ) : (
                                               <div className="text-center py-5">
                                                 <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                                      style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                                                   <FeatherIcon icon="file-text" size={24} className="text-muted" />
                                                 </div>
                                                 <h5 className="text-muted">{t('business.components.asset_modal.documents.empty_title', 'No documents found')}</h5>
                                                 <p className="text-muted mb-0">{t('business.components.asset_modal.documents.empty_message', 'No documents have been uploaded for this asset')}</p>
                                               </div>
                                            )}
                                          </CardBody>
                                        </Card>
                                      </div>
                                    </TabPane>

              </TabContent>
            </div>
          </motion.div>
        </Modal>
      )}


    {/* Sub Asset Delete Modal */}
    <Modal isOpen={subAssetDeleteModal.isOpen} toggle={() => setSubAssetDeleteModal({ isOpen: false, subAsset: null })}>
      <div className="modal-header">
        <h5 className="modal-title">{t('business.components.asset_modal.messages.confirm_delete_sub_asset', 'Confirm Delete Sub Asset')}</h5>
        <button 
          type="button" 
          className="btn-close" 
          onClick={() => setSubAssetDeleteModal({ isOpen: false, subAsset: null })} 
          aria-label="Close"
        ></button>
      </div>
      <div className="modal-body">
        <p>
          <Trans 
            i18nKey="business.components.asset_modal.messages.confirm_delete_sub_asset_message"
            values={{ subAssetName: subAssetDeleteModal.subAsset?.assetName }}
            defaults="Are you sure you want to delete the sub asset <strong>{{subAssetName}}</strong>? This action cannot be undone."
            components={{ strong: <strong /> }}
          />
        </p>
      </div>
      <div className="modal-footer">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={() => setSubAssetDeleteModal({ isOpen: false, subAsset: null })}
        >
          {t('shared.cancel', 'Cancel')}
        </button>
        <button 
          type="button" 
          className="btn btn-danger" 
          onClick={confirmDeleteSubAsset}
          disabled={loadingSubAssets}
        >
          {loadingSubAssets ? (
            <>
              <Spinner size="sm" className="me-1" />
              {t('shared.deleting', 'Deleting...')}
            </>
          ) : (
            t('shared.delete', 'Delete')
          )}
        </button>
      </div>
    </Modal>

    <AnimatePresence>
  {warrantyModal && (
    <Modal
      size="lg"
      isOpen={warrantyModal}
      toggle={handleWarrantyModal}
      backdrop="static"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="modal-header">
          <h5 className="modal-title">
            {selectedWarranty?.id ? t('shared.edit_service_contract', 'Edit Service Contract') : t('shared.add_service_contract', 'Add Service Contract')}
          </h5>
          <button
            onClick={handleWarrantyModal}
            type="button"
            className="btn-close"
            aria-label="Close"
          ></button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleWarrantyUpdate}>
            
            {/* Manufacturer Warranty Section */}
            <div className="mb-4">
              <h6 className="text-primary mb-3">{t('shared.manufacturer_warranty', 'Manufacturer Warranty')}</h6>
              
              <Row className="mb-3">
                <label className="col-sm-3 col-form-label">
                  {t('shared.manufacturer_warranty', 'Manufacturer Warranty')}
                </label>
                <Col sm={9}>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={selectedWarranty?.hasManufacturerWarranty || false}
                      onChange={(e) =>
                        setSelectedWarranty({
                          ...selectedWarranty,
                          hasManufacturerWarranty: e.target.checked,
                        })
                      }
                    />
                    <label className="form-check-label">
                      {selectedWarranty?.hasManufacturerWarranty ? t('shared.yes', 'Yes') : t('shared.no', 'No')}
                    </label>
                  </div>
                </Col>
              </Row>

              {selectedWarranty?.hasManufacturerWarranty && (
                <>
                  <Row className="mb-3">
                    <label className="col-sm-3 col-form-label">
                      {t('shared.warranty_start_date', 'Warranty Start Date')}
                    </label>
                    <Col sm={9}>
                      <input
                        type="date"
                        value={selectedWarranty?.manufacturerWarrantyStartDate ?
                          selectedWarranty.manufacturerWarrantyStartDate.split('T')[0] : ""}
                        onChange={(e) =>
                          setSelectedWarranty({
                            ...selectedWarranty,
                            manufacturerWarrantyStartDate: e.target.value+ "T00:00:00",
                          })
                        }
                        className={`form-control ${warrantyErrors.manufacturerWarrantyStartDate ? 'is-invalid' : ''}`}
                      />
                      {warrantyErrors.manufacturerWarrantyStartDate && (
                        <small className="text-danger">{warrantyErrors.manufacturerWarrantyStartDate}</small>
                      )}
                    </Col>
                  </Row>
                  
                  <Row className="mb-3">
                    <label className="col-sm-3 col-form-label">
                      {t('shared.warranty_end_date', 'Warranty End Date')}
                    </label>
                    <Col sm={9}>
                      <input
                        type="date"
                        value={selectedWarranty?.manufacturerWarrantyEndDate ?
                          selectedWarranty.manufacturerWarrantyEndDate.split('T')[0] : ""}
                        onChange={(e) =>
                          setSelectedWarranty({
                            ...selectedWarranty,
                            manufacturerWarrantyEndDate: e.target.value+ "T00:00:00",
                          })
                        }
                        className={`form-control ${warrantyErrors.manufacturerWarrantyEndDate ? 'is-invalid' : ''}`}
                      />
                      {warrantyErrors.manufacturerWarrantyEndDate && (
                        <small className="text-danger">{warrantyErrors.manufacturerWarrantyEndDate}</small>
                      )}
                    </Col>
                  </Row>
                </>
              )}
            </div>

            <hr />

            {/* Warranty Extension Section */}
            <div className="mb-4">
              <h6 className="text-primary mb-3">{t('shared.warranty_extension', 'Warranty Extension')}</h6>
              
              <Row className="mb-3">
                <label className="col-sm-3 col-form-label">
                  {t('shared.warranty_extension', 'Warranty Extension')}
                </label>
                <Col sm={9}>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={selectedWarranty?.hasWarrantyExtension || false}
                      onChange={(e) =>
                        setSelectedWarranty({
                          ...selectedWarranty,
                          hasWarrantyExtension: e.target.checked,
                        })
                      }
                    />
                    <label className="form-check-label">
                      {selectedWarranty?.hasWarrantyExtension ? t('shared.yes', 'Yes') : t('shared.no', 'No')}
                    </label>
                  </div>
                </Col>
              </Row>

              {selectedWarranty?.hasWarrantyExtension && (
                <>
                  <Row className="mb-3">
                    <label className="col-sm-3 col-form-label">
                      {t('shared.extension_start_date', 'Extension Start Date')}
                    </label>
                    <Col sm={9}>
                      <input
                        type="date"
                        value={selectedWarranty?.warrantyExtensionStartDate ?
                          selectedWarranty.warrantyExtensionStartDate.split('T')[0] : ""}
                        onChange={(e) =>
                          setSelectedWarranty({
                            ...selectedWarranty,
                            warrantyExtensionStartDate: e.target.value + "T00:00:00",
                          })
                        }
                        className={`form-control ${warrantyErrors.warrantyExtensionStartDate ? 'is-invalid' : ''}`}
                      />
                      {warrantyErrors.warrantyExtensionStartDate && (
                        <small className="text-danger">{warrantyErrors.warrantyExtensionStartDate}</small>
                      )}
                    </Col>
                  </Row>
                  
                  <Row className="mb-3">
                    <label className="col-sm-3 col-form-label">
                      {t('shared.extension_end_date', 'Extension End Date')}
                    </label>
                    <Col sm={9}>
                      <input
                        type="date"
                        value={selectedWarranty?.warrantyExtensionEndDate ?
                          selectedWarranty.warrantyExtensionEndDate.split('T')[0] : ""}
                        onChange={(e) =>
                          setSelectedWarranty({
                            ...selectedWarranty,
                            warrantyExtensionEndDate: e.target.value+ "T00:00:00",
                          })
                        }
                        className={`form-control ${warrantyErrors.warrantyExtensionEndDate ? 'is-invalid' : ''}`}
                      />
                      {warrantyErrors.warrantyExtensionEndDate && (
                        <small className="text-danger">{warrantyErrors.warrantyExtensionEndDate}</small>
                      )}
                    </Col>
                  </Row>
                </>
              )}
            </div>

            <hr />

            {/* Maintenance Contract Section */}
            <div className="mb-4">
              <h6 className="text-primary mb-3">{t('shared.maintenance_contract', 'Maintenance Contract')}</h6>
              
              <Row className="mb-3">
                <label className="col-sm-3 col-form-label">
                  {t('shared.maintenance_contract', 'Maintenance Contract')}
                </label>
                <Col sm={9}>
                  <select
                    value={selectedWarranty?.maintenanceContractType || "NO_CONTRACT"}
                    onChange={(e) =>
                      setSelectedWarranty({
                        ...selectedWarranty,
                        maintenanceContractType: e.target.value,
                      })
                    }
                    className="form-select"
                  >
                    <option value="NO_CONTRACT" selected>{t('shared.maintenance_contract_types_no_contract', 'No Contract')}</option>
                    <option value="AMC">{t('shared.maintenance_contract_types_amc', 'AMC (Annual Maintenance Contract)')}</option>
                    <option value="CMC">{t('shared.maintenance_contract_types_cmc', 'CMC (Comprehensive Maintenance Contract)')}</option>
                  </select>
                </Col>
              </Row>

              {selectedWarranty?.maintenanceContractType && selectedWarranty?.maintenanceContractType !== "NO_CONTRACT" && (
                <>
                  <Row className="mb-3">
                    <label className="col-sm-3 col-form-label">
                      {(() => {
                        const type = selectedWarranty?.maintenanceContractType || '';
                        const typeKey = type.split(' ').map(word => word[0]?.toLowerCase()).filter(char => char && char.match(/[a-z]/i)).join('');
                        return t(`shared.maintenance_contract_types_${typeKey}`, type);
                      })()} {t('shared.vendor_name', 'Vendor Name')}
                    </label>
                    <Col sm={9}>
                      <select
                        value={selectedWarranty?.vendorId || ""}
                        onChange={(e) =>
                          setSelectedWarranty({
                            ...selectedWarranty,
                            vendorId: e.target.value,
                          })
                        }
                        className={`form-select ${warrantyErrors.vendorId ? 'is-invalid' : ''}`}
                      >
                        <option value="">{t('shared.select_vendor', 'Select Vendor')}</option>
                        {vendors?.map((vendor,index) => (
                          <option key={vendor.id || index} value={vendor.id}>
                            {vendor.vendorName}
                          </option>
                        ))}
                      </select>
                      {warrantyErrors.vendorId && (
                        <small className="text-danger">{warrantyErrors.vendorId}</small>
                      )}
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <label className="col-sm-3 col-form-label">
                      {t('shared.contract_number', 'Contract Number')}
                    </label>
                    <Col sm={9}>
                      <input
                        type="text"
                        value={selectedWarranty?.contractNumber || ""}
                        onChange={(e) =>
                          setSelectedWarranty({
                            ...selectedWarranty,
                            contractNumber: e.target.value,
                          })
                        }
                        className={`form-control ${warrantyErrors.contractNumber ? 'is-invalid' : ''}`}
                        placeholder={t('shared.enter_contract_number', 'Enter contract number')}
                      />
                      {warrantyErrors.contractNumber && (
                        <small className="text-danger">{warrantyErrors.contractNumber}</small>
                      )}
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <label className="col-sm-3 col-form-label">
                      {t('shared.contract_start_date', 'Contract Start Date')}
                    </label>
                    <Col sm={9}>
                      <input
                        type="date"
                        value={selectedWarranty?.contractStartDate ?
                          selectedWarranty.contractStartDate.split('T')[0] : ""}
                        onChange={(e) =>
                          setSelectedWarranty({
                            ...selectedWarranty,
                            contractStartDate: e.target.value+ "T00:00:00",
                          })
                        }
                        className={`form-control ${warrantyErrors.contractStartDate ? 'is-invalid' : ''}`}
                      />
                      {warrantyErrors.contractStartDate && (
                        <small className="text-danger">{warrantyErrors.contractStartDate}</small>
                      )}
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <label className="col-sm-3 col-form-label">
                      {t('shared.contract_end_date', 'Contract End Date')}
                    </label>
                    <Col sm={9}>
                      <input
                        type="date"
                        value={selectedWarranty?.contractEndDate ?
                          selectedWarranty.contractEndDate.split('T')[0] : ""}
                        onChange={(e) =>
                          setSelectedWarranty({
                            ...selectedWarranty,
                            contractEndDate: e.target.value + "T00:00:00",
                          })
                        }
                        className={`form-control ${warrantyErrors.contractEndDate ? 'is-invalid' : ''}`}
                      />
                      {warrantyErrors.contractEndDate && (
                        <small className="text-danger">{warrantyErrors.contractEndDate}</small>
                      )}
                    </Col>
                  </Row>

<Row className="mb-3">
  <label className="col-sm-3 col-form-label">
    {t('shared.contract_value', 'Contract Value')}
  </label>
  <Col sm={9}>
    <div className="input-group">
      <input
        type="number"
        step="0.01"
        min="0"
        value={selectedWarranty?.contractValue || ""}
        onChange={(e) =>
          setSelectedWarranty({
            ...selectedWarranty,
            contractValue: parseFloat(e.target.value) || 0,
          })
        }
        className={`form-control ${warrantyErrors.contractValue ? 'is-invalid' : ''}`}
        placeholder={t('shared.enter_contract_value', 'Enter contract value')}
      />
      <select
        value={selectedWarranty?.contractCurrency || "USD"}
        onChange={(e) =>
          setSelectedWarranty({
            ...selectedWarranty,
            contractCurrency: e.target.value,
          })
        }
        className="form-select"
        style={{ maxWidth: '100px' }}
      >
        {Object.entries(currencies(t)).map(([code, name]) => (
          <option key={code} value={code}>
            {name}
          </option>
        ))}
      </select>
    </div>
    
    {/* Display the formatted contract value */}
    {contractValueFormatted && (
      <div className="mt-2">
        <small className="text-muted">
          {t('shared.formatted', 'Formatted')}: {contractValueFormatted}
        </small>
      </div>
    )}
    
    {warrantyErrors.contractValue && (
      <small className="text-danger">{warrantyErrors.contractValue}</small>
    )}
  </Col>
</Row>
                </>
              )}
            </div>

            {/* Notes Section */}
            <div className="mb-4">
              <h6 className="text-primary mb-3"> {t('shared.additional_notes', 'Additional Notes')}</h6>
              <Row className="mb-3">
                <label className="col-sm-3 col-form-label">
                  {t('shared.notes', 'Notes')}
                </label>
                <Col sm={9}>
                  <textarea
                    rows="3"
                    value={selectedWarranty?.notes || ""}
                    onChange={(e) =>
                      setSelectedWarranty({
                        ...selectedWarranty,
                        notes: e.target.value,
                      })
                    }
                    className="form-control"
                    placeholder={t('shared.add_notes_placeholder', 'Add any additional notes about the warranty or maintenance contract')}
                  />
                </Col>
              </Row>
            </div>

            {/* Attachments Section */}
            <div className="mb-4">
              <h6 className="text-primary mb-3"> {t('shared.attachments', 'Attachments')}</h6>
              <Row className="mb-3">
                <label className="col-sm-3 col-form-label">
                  {t('shared.upload_images', 'Upload Images')}
                </label>
                <Col sm={9}>
                  <div className="mb-3">
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*,.pdf"
                      multiple
                      disabled={uploadingImages}
                      onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        if (files.length === 0) return;

                        setUploadingImages(true);
                        try {
                          const uploadedUrls = await uploadServiceContractFiles(files);

                          const existingUrls = selectedWarranty?.attachmentUrls || [];
                          setSelectedWarranty({
                            ...selectedWarranty,
                            attachmentUrls: [...existingUrls, ...uploadedUrls]
                          });
                        } catch (err) {
                          console.error("Failed to upload files:", err);
                          setWarrantyApiError(t("shared.failed_upload", "Failed to upload files. Please try again."));
                        } finally {
                          setUploadingImages(false);
                          e.target.value = ''; // Reset input
                        }
                      }}
                    />
                    {uploadingImages && (
                      <div className="mt-2">
                        <Spinner size="sm" color="primary" /> <small className="text-muted ms-2">{t('shared.uploading_files', 'Uploading files...')}</small>
                      </div>
                    )}
                    <small className="text-muted">{t('shared.upload_images_or_pdf', 'Upload images or PDF documents only')}</small>
                  </div>
                  {selectedWarranty?.attachmentUrls && selectedWarranty.attachmentUrls.length > 0 && (
                    <div className="d-flex flex-wrap gap-2">
                      {selectedWarranty.attachmentUrls.map((url, index) => {
                        const isImage = url.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?|$)/i);
                        const isPdf = url.match(/\.pdf(\?|$)/i);
                        const isDoc = url.match(/\.(doc|docx)(\?|$)/i);
                        const isExcel = url.match(/\.(xls|xlsx)(\?|$)/i);
                        const fileName = url.split('/').pop().split('?')[0];

                        return (
                          <div key={index} className="position-relative">
                            {isImage ? (
                              <img
                                src={url}
                                alt={`Attachment ${index + 1}`}
                                className="rounded border"
                                style={{
                                  width: '100px',
                                  height: '100px',
                                  objectFit: 'cover',
                                  cursor: 'pointer'
                                }}
                                onClick={() => window.open(url, '_blank')}
                              />
                            ) : (
                              <div
                                className="rounded border d-flex flex-column align-items-center justify-content-center p-2"
                                style={{
                                  width: '100px',
                                  height: '100px',
                                  cursor: 'pointer',
                                  backgroundColor: '#f8f9fa'
                                }}
                                onClick={() => window.open(url, '_blank')}
                              >
                                <FeatherIcon
                                  icon={
                                    isPdf ? 'file-text' :
                                    isDoc ? 'file-text' :
                                    isExcel ? 'grid' :
                                    'file'
                                  }
                                  size={32}
                                  className="mb-1"
                                  style={{ color: '#6c757d' }}
                                />
                                <small className="text-muted text-center" style={{ wordBreak: 'break-word', fontSize: '9px' }}>
                                  {fileName.length > 15 ? fileName.substring(0, 12) + '...' : fileName}
                                </small>
                              </div>
                            )}
                            <Button
                              type="button"
                              color="danger"
                              size="sm"
                              className="position-absolute top-0 end-0 m-1"
                              style={{ padding: '2px 6px', fontSize: '10px' }}
                              onClick={() => {
                                const newUrls = selectedWarranty.attachmentUrls.filter((_, i) => i !== index);
                                setSelectedWarranty({
                                  ...selectedWarranty,
                                  attachmentUrls: newUrls
                                });
                              }}
                            >
                              <FeatherIcon icon="x" size={12} />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Col>
              </Row>
            </div>

            {/* Error Message */}
            {warrantyApiError && (
              <div className="alert alert-danger" role="alert">
                {warrantyApiError}
              </div>
            )}

            {/* Action Buttons */}
            <div className="d-flex justify-content-end gap-2">
              <Button
                type="button"
                color="secondary"
                onClick={handleWarrantyModal}
              >
                {t('shared.cancel', 'Cancel')}
              </Button>
              <Button
                type="submit"
                color="primary"
              >
                {selectedWarranty?.id ? t("shared.update_service_contract", "Update Service Contract") : t("shared.add_service_contract", "Add Service Contract")} 
              </Button>
            </div>
          </form>
        </div>
      </motion.div>
    </Modal>
  )}
</AnimatePresence>

  <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
      <div className="modal-header">
          <h5 className="modal-title">{t('shared.confirm_delete', 'Confirm Delete')}</h5>
          <button type="button" className="btn-close" onClick={() => setDeleteModal(false)} aria-label={t('shared.close', 'Close')}></button>
      </div>
      <div className="modal-body">
          <p>{t('shared.are_you_sure_you_want_to_delete_this_ass', 'Are you sure you want to delete this asset? This action cannot be undone.')}</p>
      </div>
      <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setDeleteModal(false)}>
              {t('shared.cancel', 'Cancel')}
          </button>
          <button 
              type="button" 
              className="btn btn-danger" 
              onClick={handleDelete}
          >
              {t('shared.delete', 'Delete')}
          </button>
      </div>
  </Modal>

      {/* Image Modal for full-screen view */}
      <AnimatePresence>
        {showImageModal && assetData?.assetImageUrls && assetData.assetImageUrls.length > 0 && (
          <Modal
            isOpen={showImageModal}
            toggle={closeImageModal}
            size="lg"
            centered
            className="image-modal"
            backdrop="static"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="modal-content border-0"
              style={{ backgroundColor: 'rgba(0,0,0,0.9)' }}
            >
              <div className="modal-header border-0 bg-transparent text-white">
                <h5 className="modal-title">
                  {t('shared.asset_image', 'Asset Image')} {selectedImageIndex + 1} of {assetData.assetImageUrls.length}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={closeImageModal}
                  aria-label="Close"
                ></button>
              </div>
              
              <div className="modal-body p-0 d-flex align-items-center justify-content-center position-relative">
                {/* Navigation Arrows */}
                {assetData.assetImageUrls.length > 1 && (
                  <>
                    <button
                      className="btn btn-outline-light position-absolute start-0 ms-3 rounded-circle"
                      style={{ zIndex: 10, width: '50px', height: '50px' }}
                      onClick={prevImage}
                    >
                      <i className="bx bx-chevron-left fs-4"></i>
                    </button>
                    <button
                      className="btn btn-outline-light position-absolute end-0 me-3 rounded-circle"
                      style={{ zIndex: 10, width: '50px', height: '50px' }}
                      onClick={nextImage}
                    >
                      <i className="bx bx-chevron-right fs-4"></i>
                    </button>
                  </>
                )}

                {/* Main Image */}
                <motion.img
                  key={selectedImageIndex}
                  src={assetData.assetImageUrls[selectedImageIndex]}
                  alt={`Asset image ${selectedImageIndex + 1}`}
                  className="img-fluid"
                  style={{
                    maxHeight: '70vh',
                    maxWidth: '100%',
                    objectFit: 'contain'
                  }}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  onError={(e) => {
                  }}
                />
              </div>

              {/* Image Navigation Dots */}
              {assetData.assetImageUrls.length > 1 && (
                <div className="modal-footer border-0 bg-transparent d-flex justify-content-center">
                  <div className="d-flex gap-2">
                    {assetData.assetImageUrls.map((_, index) => (
                      <button
                        key={index}
                        className={`btn rounded-circle p-1 ${
                          index === selectedImageIndex 
                            ? 'btn-light' 
                            : 'btn-outline-light'
                        }`}
                        style={{ width: '12px', height: '12px' }}
                        onClick={() => setSelectedImageIndex(index)}
                      ></button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Assign Service Contract Modal */}
      <Modal
        isOpen={assignContractModal}
        toggle={handleAssignContractModal}
        centered
        size="md"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="bx bx-link me-2"></i>
              {t('business.components.asset_modal.assign_contract.title', 'Assign Service Contract')}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleAssignContractModal}
              aria-label={t('shared.close', 'Close')}
            ></button>
          </div>

          <div className="modal-body p-4">
            {assignContractError && (
              <div className="alert alert-danger" role="alert">
                {assignContractError}
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="serviceContractSelect" className="form-label fw-medium">
                {t('business.components.asset_modal.assign_contract.select_label', 'Select Service Contract')} <span className="text-danger">*</span>
              </label>
              <select
                id="serviceContractSelect"
                className="form-select"
                value={selectedContractId}
                onChange={(e) => setSelectedContractId(e.target.value)}
                disabled={loadingContracts}
              >
                <option value="">
                  {loadingContracts ? t('shared.loading_contracts', "Loading contracts...") : t('shared.choose_contract_placeholder', "Choose a service contract")}
                </option>
                {allServiceContracts.map((contract, index) => {
                  const assetNames = contract.asset?.map(a => a.name).join(', ') || 'No assets';
                  const contractType = contract.maintenanceContractTypeDisplay || contract.maintenanceContractType;
                  const contractNumber = contract.contractNumber || 'N/A';

                  return (
                    <option key={contract.id || `contract-${index}`} value={contract.id}>
                      {contractType} - {contractNumber} ({assetNames})
                    </option>
                  );
                })}
              </select>
              <small className="text-muted">
                {t('business.components.asset_modal.assign_contract.help_text', 'Select an existing service contract to assign to this asset')}
              </small>
            </div>

            {loadingContracts && (
              <div className="text-center py-3">
                <Spinner color="primary" size="sm" />
                <p className="text-muted mt-2 mb-0">{t('shared.loading_contracts_message', 'Loading service contracts...')}</p>
              </div>
            )}

            {!loadingContracts && allServiceContracts.length === 0 && (
              <div className="alert alert-info">
                <i className="bx bx-info-circle me-2"></i>
                {t('business.components.asset_modal.assign_contract.no_contracts', 'No service contracts available. Please create a service contract first.')}
              </div>
            )}
          </div>

          <div className="modal-footer bg-light">
            <Button
              color="secondary"
              onClick={handleAssignContractModal}
              disabled={assigningContract}
            >
              {t('shared.cancel', 'Cancel')}
            </Button>
            <Button
              color="primary"
              onClick={handleAssignContract}
              disabled={assigningContract || !selectedContractId}
            >
              {assigningContract ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t('shared.assigning', 'Assigning...')}
                </>
              ) : (
                <>
                  <i className="bx bx-check me-1"></i>
                  {t('shared.assign_contract', 'Assign Contract')}
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </Modal>

      {/* Preventive Maintenance Modal */}
      <PreventiveModal
        isOpen={preventiveModal.isOpen}
        toggle={() => setPreventiveModal({ isOpen: false, workOrder: null })}
        workOrder={preventiveModal.workOrder}
        onRefresh={fetchAllWorkOrders}
      />

      {/* Meter Detail Modal */}
      <MeterDetailModal
        isOpen={!!detailMeterId}
        toggle={() => setDetailMeterId(null)}
        meterId={detailMeterId}
        onUpdate={fetchAssetMeters}
      />

      {/* Status Change Modal */}
      <AssetStatusChangeModal
        isOpen={showStatusModal}
        toggle={() => setShowStatusModal(false)}
        asset={assetData}
        onSuccess={refreshAssetData}
      />

      <AssetTransferModal
        isOpen={showTransferModal}
        toggle={() => setShowTransferModal(false)}
        asset={assetData}
        onSuccess={refreshAssetData}
      />

      {/* Transfer details — same modal as the Planning page, opened from a Planning row */}
      <TransferDetailsModal
        isOpen={transferDetails.isOpen}
        toggle={() => setTransferDetails({ isOpen: false, transfer: null })}
        transfer={transferDetails.transfer}
        onUpdated={(updated) => {
          if (updated) setTransferDetails((prev) => ({ ...prev, transfer: updated }));
          refreshAssetData();
        }}
      />

      {/* Location details for a clicked Planning-row location */}
      <LocationModal
        isOpen={planningLocationModal.isOpen}
        toggle={() => setPlanningLocationModal((prev) => ({ ...prev, isOpen: false }))}
        title={planningLocationModal.title}
        mainData={planningLocationModal.mainData}
        relatedData={planningLocationModal.relatedData}
        loading={planningLocationModal.loading}
        formatCreatedDate={formatCreatedDate}
        locationData={planningLocationModal.locationData}
      />

      {/* Attach Spare Part Modal */}
      <Modal
        isOpen={showAttachSparePartModal}
        toggle={() => setShowAttachSparePartModal(false)}
        centered
      >
        <div className="modal-header">
          <h5 className="modal-title">
            {t("business.asset_detail.attach_spare_part", "Attach Spare Part")}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setShowAttachSparePartModal(false)}
          />
        </div>
        <div className="modal-body">
          <div className="mb-3">
            <label className="form-label">
              {t("business.asset_detail.select_spare_part", "Select Spare Part")}
            </label>
            <Input
              type="select"
              value={selectedSparePartId}
              onChange={(e) => setSelectedSparePartId(e.target.value)}
            >
              <option value="">
                {t("business.asset_detail.select_spare_part_placeholder", "-- Select a spare part --")}
              </option>
              {availableSpareParts
                .filter((sp) => sp.quantity > 0)
                .map((sp) => (
                  <option key={sp.id} value={sp.id}>
                    {sp.name} ({t("shared.available", "Available")}: {sp.available ?? sp.quantity})
                  </option>
                ))}
            </Input>
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("shared.quantity", "Quantity")}
            </label>
            <Input
              type="number"
              min={1}
              max={
                availableSpareParts.find(
                  (sp) => String(sp.id) === String(selectedSparePartId)
                )?.available ??
                availableSpareParts.find(
                  (sp) => String(sp.id) === String(selectedSparePartId)
                )?.quantity ??
                999
              }
              value={attachQuantity}
              onChange={(e) => setAttachQuantity(Number(e.target.value))}
            />
          </div>
        </div>
        <div className="modal-footer">
          <Button
            color="secondary"
            onClick={() => setShowAttachSparePartModal(false)}
          >
            {t("shared.cancel", "Cancel")}
          </Button>
          <Button
            color="primary"
            disabled={!selectedSparePartId || attachQuantity < 1 || attachLoading}
            onClick={handleAttachSparePart}
          >
            {attachLoading ? (
              <Spinner size="sm" className="me-1" />
            ) : null}
            {t("business.asset_detail.attach", "Attach")}
          </Button>
        </div>
      </Modal>
    </AnimatePresence>
  );
};

export default AssetModal;